import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import ErrorBoundary from '../components/ErrorBoundary';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { LeagueAccuracyModal } from '../components/LeagueAccuracyModal';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Users,
  Activity,
  TrendingUp,
  TrendingDown,
  Calendar,
  Radio,
  CheckCircle2,
  Clock,
  RefreshCw,
  Settings,
  Database,
  BarChart3,
  Target,
  Shield,
  AlertTriangle,
  AlertCircle,
  Loader2,
  Trophy,
  DollarSign,
  Save,
  UserCog,
  ChevronRight,
  Upload,
  FileJson,
  FileSpreadsheet,
  History,
  ArrowRight,
  Clock as ClockIcon,
  Gift,
  Brain,
  Bug,
  Mail,
  UserX,
  UserCheck,
  Search,
  ChevronLeft,
  Eye,
  X,
  Send,
  MessageCircle,
  Download,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import AILearningDashboard from '../components/AILearningDashboard';
import BugScanner from '../components/BugScanner';
import AdminMessages from '../components/AdminMessages';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// System Health Dashboard Component
function SystemHealthDashboard() {
  const [healthData, setHealthData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchHealthData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/system-health`, {
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setHealthData(data);
      }
    } catch (err) {
      console.error('Failed to fetch system health:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchHealthData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchHealthData, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchHealthData();
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
        </div>
      </div>
    );
  }

  const healthScore = healthData?.health_score || {};
  const apiMetrics = healthData?.api_response_times || {};
  const dbStatus = healthData?.database_status || {};
  const taskSummary = healthData?.background_tasks || {};
  const schedulerStatus = healthData?.scheduler_status || {};
  const recommendations = healthData?.recommendations || [];

  const getGradeColor = (grade) => {
    switch(grade) {
      case 'A': return 'text-green-400';
      case 'B': return 'text-blue-400';
      case 'C': return 'text-yellow-400';
      case 'D': return 'text-orange-400';
      case 'F': return 'text-red-400';
      default: return 'text-muted-foreground';
    }
  };

  return (
    <div className="space-y-6">
      {/* Health Score Banner */}
      <Card className={`${healthScore.status === 'healthy' ? 'border-green-500/50 bg-green-500/10' : healthScore.status === 'degraded' ? 'border-yellow-500/50 bg-yellow-500/10' : 'border-red-500/50 bg-red-500/10'}`}>
        <CardContent className="py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`text-6xl font-bold ${getGradeColor(healthScore.grade)}`}>
                {healthScore.grade || '-'}
              </div>
              <div>
                <h2 className="text-2xl font-bold">System Health Score: {healthScore.score || 0}/100</h2>
                <p className="text-muted-foreground">
                  Status: <span className={healthScore.status === 'healthy' ? 'text-green-400' : healthScore.status === 'degraded' ? 'text-yellow-400' : 'text-red-400'}>
                    {healthScore.status?.toUpperCase() || 'UNKNOWN'}
                  </span>
                </p>
              </div>
            </div>
            <Button onClick={handleRefresh} disabled={refreshing} variant="outline">
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
          {healthScore.issues?.length > 0 && (
            <div className="mt-4 space-y-1">
              {healthScore.issues.map((issue, idx) => (
                <p key={idx} className="text-sm text-yellow-400 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" /> {issue}
                </p>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* API Response Time */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Activity className="w-4 h-4" /> API Response Time
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{apiMetrics.avg_ms || 0}ms</div>
            <p className="text-xs text-muted-foreground">Average ({apiMetrics.sample_size || 0} requests)</p>
            <div className="mt-2 text-xs space-y-1">
              <p>P95: {apiMetrics.p95_ms || 0}ms</p>
              <p>P99: {apiMetrics.p99_ms || 0}ms</p>
            </div>
          </CardContent>
        </Card>

        {/* Database Latency */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Database className="w-4 h-4" /> Database
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{dbStatus.latency_ms || 0}ms</div>
            <p className="text-xs text-muted-foreground">
              <span className={dbStatus.status === 'connected' ? 'text-green-400' : 'text-red-400'}>
                {dbStatus.status?.toUpperCase() || 'UNKNOWN'}
              </span>
            </p>
            <div className="mt-2 text-xs space-y-1">
              <p>Games: {dbStatus.collections?.games?.toLocaleString() || 0}</p>
              <p>Users: {dbStatus.collections?.users || 0}</p>
            </div>
          </CardContent>
        </Card>

        {/* Background Tasks */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Clock className="w-4 h-4" /> Background Tasks
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-400">{taskSummary.success_count || 0}</div>
            <p className="text-xs text-muted-foreground">Completed tasks</p>
            <div className="mt-2 text-xs space-y-1">
              <p className="text-red-400">Failed: {taskSummary.failed_count || 0}</p>
              <p className="text-blue-400">Running: {taskSummary.running_count || 0}</p>
            </div>
          </CardContent>
        </Card>

        {/* Run All Fixes Status */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Settings className="w-4 h-4" /> Run All Fixes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`text-lg font-bold ${schedulerStatus.run_all_fixes?.running ? 'text-blue-400' : 'text-green-400'}`}>
              {schedulerStatus.run_all_fixes?.running ? 'RUNNING' : 'IDLE'}
            </div>
            <p className="text-xs text-muted-foreground truncate">
              {schedulerStatus.run_all_fixes?.last_step || 'N/A'}
            </p>
            {schedulerStatus.run_all_fixes?.last_run && (
              <p className="text-xs mt-2">
                Last: {new Date(schedulerStatus.run_all_fixes.last_run).toLocaleString()}
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Slow Endpoints */}
      {apiMetrics.slow_endpoints?.length > 0 && (
        <Card className="border-yellow-500/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-400" />
              Slow Endpoints (&gt;500ms avg)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {apiMetrics.slow_endpoints.map((ep, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 bg-background/50 rounded">
                  <code className="text-sm">{ep.endpoint}</code>
                  <div className="flex items-center gap-4">
                    <span className="text-sm">{ep.calls} calls</span>
                    <Badge variant={ep.status === 'slow' ? 'destructive' : 'outline'}>
                      {ep.avg_ms}ms
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Tasks */}
      {taskSummary.recent_tasks?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Recent Background Tasks
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {taskSummary.recent_tasks.slice().reverse().map((task, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 bg-background/50 rounded">
                  <div className="flex items-center gap-2">
                    <Badge variant={task.status === 'completed' ? 'default' : task.status === 'failed' ? 'destructive' : 'secondary'}>
                      {task.status}
                    </Badge>
                    <span className="text-sm font-medium">{task.task_name}</span>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    {task.duration_sec && <span>{task.duration_sec}s</span>}
                    <span>{new Date(task.timestamp).toLocaleTimeString()}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recommendations */}
      {recommendations.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="w-5 h-5" />
              Performance Recommendations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {recommendations.map((rec, idx) => (
                <div key={idx} className={`p-3 rounded flex items-start gap-3 ${rec.type === 'warning' ? 'bg-yellow-500/10 border border-yellow-500/30' : 'bg-blue-500/10 border border-blue-500/30'}`}>
                  {rec.type === 'warning' ? (
                    <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0" />
                  )}
                  <span className="text-sm">{rec.message}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Scheduler Status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Scheduled Jobs Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-3 bg-background/50 rounded">
              <h4 className="font-medium mb-1">Live Score Refresh</h4>
              <p className="text-xs text-muted-foreground">{schedulerStatus.live_score_refresh?.description}</p>
              <Badge variant="outline" className="mt-2">{schedulerStatus.live_score_refresh?.interval}</Badge>
            </div>
            <div className="p-3 bg-background/50 rounded">
              <h4 className="font-medium mb-1">Pregame Prediction Update</h4>
              <p className="text-xs text-muted-foreground">{schedulerStatus.pregame_prediction_update?.description}</p>
              <Badge variant="outline" className="mt-2">{schedulerStatus.pregame_prediction_update?.interval}</Badge>
            </div>
            <div className="p-3 bg-background/50 rounded">
              <h4 className="font-medium mb-1">ESPN Data Refresh</h4>
              <p className="text-xs text-muted-foreground">
                {schedulerStatus.espn_data_refresh?.last_refresh 
                  ? `Last: ${new Date(schedulerStatus.espn_data_refresh.last_refresh).toLocaleString()}`
                  : 'Never run'}
              </p>
              <Badge variant={schedulerStatus.espn_data_refresh?.status === 'ok' ? 'default' : 'secondary'} className="mt-2">
                {schedulerStatus.espn_data_refresh?.status?.toUpperCase()}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground text-center">
        Last updated: {healthData?.timestamp ? new Date(healthData.timestamp).toLocaleString() : 'N/A'}
        {' | '}API Version: {healthData?.api_version || 'Unknown'}
      </p>
    </div>
  );
}

function AdminDashboard() {
  const navigate = useNavigate();
  const { isAuthenticated, user, loading: authLoading } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [recentActivity, setRecentActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dashboardError, setDashboardError] = useState(null);
  const [initialAuthCheck, setInitialAuthCheck] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [triggeringRefresh, setTriggeringRefresh] = useState(false);
  const [allStandings, setAllStandings] = useState(null);
  const [loadingStandings, setLoadingStandings] = useState(false);
  
  // Pricing state
  const [pricing, setPricing] = useState(null);
  const [pricingForm, setPricingForm] = useState({
    subscription_price: 9.99,
    ai_analysis_price: 0.99,
    referrer_credit_percent: 10,
    referee_credit_percent: 10,
    trial_days: 15
  });
  const [savingPricing, setSavingPricing] = useState(false);
  
  // Admin/User view toggle
  const [viewAsUser, setViewAsUser] = useState(false);
  
  // Data Upload state
  const [uploadType, setUploadType] = useState('json');
  const [jsonData, setJsonData] = useState('');
  const [csvFile, setCsvFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  
  // Prediction Changes state
  const [predictionChanges, setPredictionChanges] = useState([]);
  const [predictionChangesLoading, setPredictionChangesLoading] = useState(false);
  const [predictionChangesSummary, setPredictionChangesSummary] = useState(null);
  const [selectedGameChanges, setSelectedGameChanges] = useState(null);
  const [showGameChangesModal, setShowGameChangesModal] = useState(false);
  const [predictionChangesFilter, setPredictionChangesFilter] = useState({
    league_id: 'all',
    change_type: 'all'
  });
  
  // League Visibility Settings state
  const [leagueVisibility, setLeagueVisibility] = useState(null);
  const [loadingLeagueVisibility, setLoadingLeagueVisibility] = useState(false);
  const [togglingLeague, setTogglingLeague] = useState(null);
  
  // Regenerate Predictions state
  const [regeneratingPredictions, setRegeneratingPredictions] = useState(false);
  const [regenerationStatus, setRegenerationStatus] = useState(null);
  
  // User Management state
  const [usersList, setUsersList] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersPagination, setUsersPagination] = useState({
    page: 1,
    per_page: 20,
    total: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false
  });
  const [usersSearch, setUsersSearch] = useState('');
  const [usersFilter, setUsersFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [userDetailLoading, setUserDetailLoading] = useState(false);
  const [sendingReminder, setSendingReminder] = useState(null);
  const [togglingUserStatus, setTogglingUserStatus] = useState(null);
  const [togglingFreeAccount, setTogglingFreeAccount] = useState(null);
  
  // Integration Status state
  const [integrationStatus, setIntegrationStatus] = useState(null);
  
  // Last Run All Fixes state
  const [lastRunAllFixes, setLastRunAllFixes] = useState(null);
  const [loadingLastRun, setLoadingLastRun] = useState(false);
  const [correctedGamesList, setCorrectedGamesList] = useState([]);
  const [showCorrectedGamesModal, setShowCorrectedGamesModal] = useState(false);

  // Daily Scheduler Status (1 AM MST) state
  const [dailySchedulerStatus, setDailySchedulerStatus] = useState(null);
  const [loadingSchedulerStatus, setLoadingSchedulerStatus] = useState(false);
  const [autoRetrying, setAutoRetrying] = useState(false);

  // Revenue Dashboard state
  const [revenueData, setRevenueData] = useState(null);
  const [revenueLoading, setRevenueLoading] = useState(false);
  const [bulkEmailSubject, setBulkEmailSubject] = useState('Special Offer from SportsAnalysisAB!');
  const [bulkEmailDiscount, setBulkEmailDiscount] = useState(0);
  const [bulkEmailTrialDays, setBulkEmailTrialDays] = useState(0);
  const [bulkEmailMessage, setBulkEmailMessage] = useState('');
  const [sendingBulkEmail, setSendingBulkEmail] = useState(false);
  const [selectedNonSubscribers, setSelectedNonSubscribers] = useState([]);

  const fetchRevenueData = async () => {
    setRevenueLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/revenue/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setRevenueData(data);
      }
    } catch (err) {
      console.error('Failed to fetch revenue data:', err);
    } finally {
      setRevenueLoading(false);
    }
  };

  const sendBulkMarketingEmail = async () => {
    setSendingBulkEmail(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/bulk-email/non-subscribers`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify({
          subject: bulkEmailSubject,
          discount_percent: bulkEmailDiscount,
          free_trial_days: bulkEmailTrialDays,
          custom_message: bulkEmailMessage,
          user_ids: selectedNonSubscribers.length > 0 ? selectedNonSubscribers : []
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setSelectedNonSubscribers([]);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send emails');
      }
    } catch (err) {
      console.error('Failed to send bulk email:', err);
      toast.error('Failed to send marketing emails');
    } finally {
      setSendingBulkEmail(false);
    }
  };

  const fetchLastRunAllFixes = async () => {
    setLoadingLastRun(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/last-run-all-fixes`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setLastRunAllFixes(data);
      }
    } catch (err) {
      console.error('Failed to fetch last run timestamp:', err);
    } finally {
      setLoadingLastRun(false);
    }
  };

  // Fetch Daily Scheduler Status (1 AM MST automatic updates)
  const fetchDailySchedulerStatus = async () => {
    setLoadingSchedulerStatus(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/daily-scheduler-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDailySchedulerStatus(data);
        
        // Auto-retry if last run failed and it's been more than 2 hours since failure
        if (data.last_run_status === 'failed' || data.last_run_status === 'error') {
          const lastRun = new Date(data.last_run_time);
          const now = new Date();
          const hoursSinceLastRun = (now - lastRun) / (1000 * 60 * 60);
          
          // If it's been more than 2 hours since failure, offer to retry
          if (hoursSinceLastRun > 2 && !autoRetrying) {
            console.log('[Scheduler] Last run failed, auto-retry available');
          }
        }
      }
    } catch (err) {
      console.error('Failed to fetch daily scheduler status:', err);
    } finally {
      setLoadingSchedulerStatus(false);
    }
  };

  // Manual retry for failed scheduler
  const retryDailyScheduler = async () => {
    setAutoRetrying(true);
    try {
      const token = localStorage.getItem('token');
      toast.info('Starting manual scheduler run in background...');

      // Trigger backend background retry. It runs ESPN refresh + alignment + accuracy
      // and properly updates daily_scheduler_status when done so the UI clears.
      const resp = await fetch(`${API_URL}/api/admin/daily-scheduler/manual-retry`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await resp.json().catch(() => ({}));

      if (!resp.ok) {
        toast.error(data?.detail || 'Failed to start manual run');
      } else if (data?.status === 'already_running') {
        toast.info('A manual run is already in progress');
      } else {
        toast.success('Manual scheduler run started! Status will refresh shortly.');
      }

      // Poll status a few times so the banner clears once finished
      for (let i = 0; i < 20; i++) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 6000));
        // eslint-disable-next-line no-await-in-loop
        const s = await fetch(`${API_URL}/api/admin/daily-scheduler-status`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        // eslint-disable-next-line no-await-in-loop
        const sd = await s.json().catch(() => ({}));
        if (sd?.last_run_status && sd.last_run_status !== 'running') {
          break;
        }
      }

      // Refresh status
      await fetchDailySchedulerStatus();
      await fetchLastRunAllFixes();
    } catch (err) {
      console.error('Failed to retry scheduler:', err);
      toast.error('Failed to run scheduler tasks');
    } finally {
      setAutoRetrying(false);
    }
  };

  const fetchPricing = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/pricing`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPricing(data);
        setPricingForm({
          subscription_price: data.subscription_price,
          ai_analysis_price: data.ai_analysis_price,
          referrer_credit_percent: data.referrer_credit_percent || 10,
          referee_credit_percent: data.referee_credit_percent || 10,
          trial_days: data.trial_days || 7
        });
      }
    } catch (err) {
      console.error('Failed to fetch pricing:', err);
    }
  };

  const fetchIntegrationStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/integration-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setIntegrationStatus(data);
      }
    } catch (err) {
      console.error('Failed to fetch integration status:', err);
    }
  };

  // User Management Functions
  const fetchUsers = async (page = 1, search = '', filter = 'all') => {
    setUsersLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: '20',
        search: search,
        status: filter
      });
      
      const response = await fetch(`${API_URL}/api/admin/users?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUsersList(data.users);
        setUsersPagination({
          page: data.page,
          per_page: data.per_page,
          total: data.total,
          total_pages: data.total_pages,
          has_next: data.has_next,
          has_prev: data.has_prev
        });
      }
    } catch (err) {
      console.error('Failed to fetch users:', err);
      toast.error('Failed to load users');
    } finally {
      setUsersLoading(false);
    }
  };

  const fetchUserDetail = async (userId) => {
    setUserDetailLoading(true);
    setSelectedUser(null); // Clear previous user data
    setShowUserModal(true); // Show modal immediately with loading state
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/users/${userId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSelectedUser(data);
      } else {
        toast.error('Failed to load user details');
        setShowUserModal(false);
      }
    } catch (err) {
      console.error('Failed to fetch user detail:', err);
      toast.error('Failed to load user details');
      setShowUserModal(false);
    } finally {
      setUserDetailLoading(false);
    }
  };

  const toggleUserActive = async (userId) => {
    setTogglingUserStatus(userId);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/users/${userId}/toggle-active`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        // Refresh users list
        fetchUsers(usersPagination.page, usersSearch, usersFilter);
        // Update selected user if modal is open
        if (selectedUser && selectedUser.user_id === userId) {
          setSelectedUser(prev => ({ ...prev, is_active: data.is_active }));
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update user status');
      }
    } catch (err) {
      console.error('Failed to toggle user status:', err);
      toast.error('Failed to update user status');
    } finally {
      setTogglingUserStatus(null);
    }
  };

  const sendReminderEmail = async (userId, reminderType) => {
    setSendingReminder(`${userId}-${reminderType}`);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/users/${userId}/send-reminder`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify({ type: reminderType })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send reminder');
      }
    } catch (err) {
      console.error('Failed to send reminder:', err);
      toast.error('Failed to send reminder email');
    } finally {
      setSendingReminder(null);
    }
  };

  const toggleFreeAccount = async (userId, e) => {
    // Prevent the row click event
    if (e) {
      e.stopPropagation();
    }
    
    setTogglingFreeAccount(userId);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/users/${userId}/toggle-free-account`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        // Refresh users list
        fetchUsers(usersPagination.page, usersSearch, usersFilter);
        // Update selected user if modal is open
        if (selectedUser && selectedUser.user_id === userId) {
          setSelectedUser(prev => ({ 
            ...prev, 
            has_free_account: data.has_free_account,
            is_subscriber: data.is_subscriber
          }));
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to toggle free account');
      }
    } catch (err) {
      console.error('Failed to toggle free account:', err);
      toast.error('Failed to toggle free account');
    } finally {
      setTogglingFreeAccount(null);
    }
  };

  const savePricing = async () => {
    setSavingPricing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/pricing`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify(pricingForm)
      });
      if (response.ok) {
        toast.success('Pricing updated successfully');
        fetchPricing();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to update pricing');
      }
    } catch (err) {
      toast.error('Failed to update pricing');
    } finally {
      setSavingPricing(false);
    }
  };

  const fetchStandings = async () => {
    setLoadingStandings(true);
    try {
      const response = await fetch(`${API_URL}/api/standings`);
      if (response.ok) {
        const data = await response.json();
        setAllStandings(data.standings);
      }
    } catch (err) {
      console.error('Failed to fetch standings:', err);
    } finally {
      setLoadingStandings(false);
    }
  };

  const fetchPredictionChanges = async () => {
    setPredictionChangesLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Use winner flips endpoint for that filter
      if (predictionChangesFilter.change_type === 'winner_flip') {
        let url = `${API_URL}/api/admin/prediction-changes/winner-flips?limit=50`;
        if (predictionChangesFilter.league_id !== 'all') {
          url += `&league_id=${predictionChangesFilter.league_id}`;
        }
        const changesRes = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
        if (changesRes.ok) {
          const data = await changesRes.json();
          setPredictionChanges(data.changes || []);
        }
      } else {
        // Normal prediction changes endpoint
        let url = `${API_URL}/api/admin/prediction-changes?limit=50`;
        if (predictionChangesFilter.league_id !== 'all') {
          url += `&league_id=${predictionChangesFilter.league_id}`;
        }
        if (predictionChangesFilter.change_type !== 'all') {
          url += `&change_type=${predictionChangesFilter.change_type}`;
        }
        
        const [changesRes, summaryRes] = await Promise.all([
          fetch(url, { headers: { 'Authorization': `Bearer ${token}` } }),
          fetch(`${API_URL}/api/admin/prediction-changes/summary/stats?days=7`, { 
            headers: { 'Authorization': `Bearer ${token}` } 
          })
        ]);
        
        if (changesRes.ok) {
          const data = await changesRes.json();
          setPredictionChanges(data.changes || []);
        }
        if (summaryRes.ok) {
          const summaryData = await summaryRes.json();
          setPredictionChangesSummary(summaryData);
        }
      }
    } catch (err) {
      console.error('Failed to fetch prediction changes:', err);
    } finally {
      setPredictionChangesLoading(false);
    }
  };

  const fetchGameChanges = async (gameId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/prediction-changes/${gameId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSelectedGameChanges(data);
        setShowGameChangesModal(true);
      }
    } catch (err) {
      toast.error('Failed to fetch game prediction history');
    }
  };

  const handleJsonUpload = async () => {
    if (!jsonData.trim()) {
      toast.error('Please enter JSON data');
      return;
    }
    
    let parsedData;
    try {
      parsedData = JSON.parse(jsonData);
    } catch (e) {
      toast.error('Invalid JSON format');
      return;
    }
    
    const games = Array.isArray(parsedData) ? parsedData : parsedData.games;
    if (!games || !Array.isArray(games)) {
      toast.error('JSON must contain a "games" array or be an array of games');
      return;
    }
    
    setUploading(true);
    setUploadResult(null);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/upload-board`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ games })
      });
      
      const result = await response.json();
      if (response.ok) {
        toast.success(`Successfully uploaded ${result.successful} games`);
        setUploadResult(result);
        setJsonData('');
        fetchDashboard();
      } else {
        toast.error(result.detail || 'Upload failed');
        setUploadResult({ error: result.detail || 'Upload failed' });
      }
    } catch (err) {
      toast.error('Failed to upload data');
    } finally {
      setUploading(false);
    }
  };

  const handleCsvUpload = async () => {
    if (!csvFile) {
      toast.error('Please select a CSV file');
      return;
    }
    
    setUploading(true);
    setUploadResult(null);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', csvFile);
      
      const response = await fetch(`${API_URL}/api/admin/upload-board-csv`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      const result = await response.json();
      if (response.ok) {
        toast.success(result.message);
        setUploadResult(result);
        setCsvFile(null);
        fetchDashboard();
      } else {
        toast.error(result.detail || 'Upload failed');
        setUploadResult({ error: result.detail || 'Upload failed' });
      }
    } catch (err) {
      toast.error('Failed to upload CSV');
    } finally {
      setUploading(false);
    }
  };

  const fetchDashboard = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      setDashboardError(null);
      
      const [dashboardRes, activityRes] = await Promise.all([
        fetch(`${API_URL}/api/dashboard/admin`, { 
          credentials: 'include',
          headers
        }),
        fetch(`${API_URL}/api/dashboard/admin/recent-activity?limit=10`, { 
          credentials: 'include',
          headers
        })
      ]);
      
      if (dashboardRes.ok) {
        const data = await dashboardRes.json();
        setDashboardData(data);
      } else if (dashboardRes.status === 401 || dashboardRes.status === 403) {
        setDashboardError('access_denied');
        toast.error('Admin access required');
        return;
      } else {
        setDashboardError('api_error');
        toast.error('Failed to load dashboard data');
      }
      
      if (activityRes.ok) {
        const activityData = await activityRes.json();
        setRecentActivity(activityData);
      }
    } catch (err) {
      console.error('Failed to fetch dashboard:', err);
      setDashboardError('network_error');
      toast.error('Failed to load admin dashboard - network error');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    // Wait for auth loading to complete
    if (authLoading) {
      return;
    }
    
    // Only redirect to login on INITIAL load if not authenticated
    // Don't redirect if we're already on the page and auth state changes
    if (initialAuthCheck) {
      setInitialAuthCheck(false);
      if (!isAuthenticated) {
        // Check if there's a token - if there is, the API might just be slow/failing
        const hasToken = localStorage.getItem('token');
        if (!hasToken) {
          navigate('/login');
          return;
        }
        // If we have a token but aren't authenticated, show error instead of redirecting
        console.log('[AdminDashboard] Token exists but auth failed - showing dashboard anyway');
      }
    }
    
    // Fetch data with error handling - use Promise.all for parallel loading
    const loadData = async () => {
      // Critical data first - fetchDashboard sets main loading state
      try {
        await fetchDashboard();
      } catch (e) {
        console.error('[AdminDashboard] fetchDashboard error:', e);
      }
      
      // Load remaining data in parallel for speed
      Promise.all([
        fetchStandings().catch(e => console.error('fetchStandings error:', e)),
        fetchPricing().catch(e => console.error('fetchPricing error:', e)),
        fetchPredictionChanges().catch(e => console.error('fetchPredictionChanges error:', e)),
        fetchIntegrationStatus().catch(e => console.error('fetchIntegrationStatus error:', e)),
        fetchUsers().catch(e => console.error('fetchUsers error:', e)),
        fetchLastRunAllFixes().catch(e => console.error('fetchLastRunAllFixes error:', e)),
        fetchRevenueData().catch(e => console.error('fetchRevenueData error:', e)),
        fetchLeagueVisibility().catch(e => console.error('fetchLeagueVisibility error:', e)),
        fetchDailySchedulerStatus().catch(e => console.error('fetchDailySchedulerStatus error:', e))
      ]);
    };
    
    loadData();
  }, [authLoading]); // Remove isAuthenticated from deps to prevent redirect loop

  // Refetch prediction changes when filter changes
  useEffect(() => {
    if (isAuthenticated) {
      fetchPredictionChanges();
    }
  }, [predictionChangesFilter]);

  // Refetch users when search or filter changes
  useEffect(() => {
    if (isAuthenticated) {
      const debounceTimer = setTimeout(() => {
        fetchUsers(1, usersSearch, usersFilter);
      }, 300);
      return () => clearTimeout(debounceTimer);
    }
  }, [usersSearch, usersFilter]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchDashboard();
  };

  const triggerDataRefresh = async () => {
    setTriggeringRefresh(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/refresh-games`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const result = await response.json();
        toast.success(`Data refresh completed! Last refresh: ${result.last_refresh ? new Date(result.last_refresh).toLocaleTimeString() : 'Now'}`);
        // Immediately refresh dashboard to show updated time
        await fetchDashboard();
      } else {
        const error = await response.text();
        toast.error(`Failed to trigger refresh: ${error}`);
      }
    } catch (err) {
      console.error('Refresh error:', err);
      toast.error('Failed to trigger refresh - check console');
    } finally {
      setTriggeringRefresh(false);
    }
  };

  const [fixingPredictions, setFixingPredictions] = useState(false);
  const [runningAllFixes, setRunningAllFixes] = useState(false);
  
  const fixPredictionAlignment = async () => {
    setFixingPredictions(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/fix-prediction-alignment`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(`Fixed ${data.games_fixed} predictions (${data.already_aligned} were already correct)`);
        fetchDashboard();
      } else {
        toast.error('Failed to fix predictions');
      }
    } catch (err) {
      toast.error('Failed to fix predictions');
    } finally {
      setFixingPredictions(false);
    }
  };

  const runAllFixes = async () => {
    setRunningAllFixes(true);
    try {
      const token = localStorage.getItem('token');
      
      // Start the background task
      const startResponse = await fetch(`${API_URL}/api/admin/run-all-fixes`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!startResponse.ok) {
        toast.error('Failed to start fixes');
        setRunningAllFixes(false);
        return;
      }
      
      const startData = await startResponse.json();
      
      // If task is already running, show progress
      if (startData.status === 'already_running') {
        toast.info('Fixes already in progress. Monitoring status...');
      } else if (startData.status === 'started') {
        toast.info('Started fixing... This may take 1-2 minutes.');
      }
      
      // Poll for completion
      let attempts = 0;
      const maxAttempts = 60; // 2 minutes max (polling every 2 seconds)
      
      const pollStatus = async () => {
        while (attempts < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, 2000)); // Wait 2 seconds
          attempts++;
          
          try {
            const statusResponse = await fetch(`${API_URL}/api/admin/run-all-fixes/status`, {
              credentials: 'include',
              headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (statusResponse.ok) {
              const status = await statusResponse.json();
              
              // Update progress indicator (could show in UI)
              if (status.running && status.current_step) {
                console.log(`[Run All Fixes] Step ${status.steps_completed}/${status.total_steps}: ${status.current_step}`);
              }
              
              // Check if completed
              if (!status.running && status.completed_at) {
                // Task completed - process results
                const results = status.results || {};
                let messages = [];
                
                if (results.refresh_games?.status === 'success') {
                  messages.push(`Refreshed ${results.refresh_games.total_games} games`);
                }
                if (results.fix_default_predictions?.status === 'success' && results.fix_default_predictions.games_fixed > 0) {
                  messages.push(`Fixed ${results.fix_default_predictions.games_fixed} default predictions`);
                }
                if (results.generate_scores?.status === 'success' && results.generate_scores.games_generated > 0) {
                  messages.push(`Generated ${results.generate_scores.games_generated} score predictions`);
                }
                if (results.fix_alignment?.status === 'success') {
                  messages.push(`Fixed ${results.fix_alignment.games_fixed} alignments`);
                }
                if (results.sync_predictions?.status === 'success') {
                  messages.push(`Synced predictions`);
                }
                if (results.backfill_accuracy?.status === 'success') {
                  messages.push(`Backfilled ${results.backfill_accuracy.created} predictions`);
                }
                if (results.fix_predictions_is_correct?.status === 'success') {
                  const pc = results.fix_predictions_is_correct;
                  if (pc.predictions_corrected > 0 || pc.predictions_created > 0) {
                    messages.push(`Fixed ${pc.predictions_corrected} predictions, created ${pc.predictions_created} new`);
                    if (pc.corrected_games && pc.corrected_games.length > 0) {
                      setCorrectedGamesList(pc.corrected_games);
                      setShowCorrectedGamesModal(true);
                    }
                  } else {
                    messages.push(`Verified ${pc.games_checked} predictions`);
                  }
                }
                
                if (status.error) {
                  toast.warning(`Completed with errors: ${status.error}`);
                } else {
                  toast.success(`All fixes completed! ${messages.join(', ')}`);
                }
                
                fetchDashboard();
                fetchLastRunAllFixes();
                return; // Exit polling
              }
            }
          } catch (pollErr) {
            console.error('Error polling status:', pollErr);
          }
        }
        
        // Timeout reached
        toast.warning('Fix all taking longer than expected. Check status later.');
      };
      
      await pollStatus();
      
    } catch (err) {
      console.error('Run all fixes error:', err);
      toast.error('Failed to run all fixes');
    } finally {
      setRunningAllFixes(false);
    }
  };

  const triggerLiveScoreRefresh = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/refresh-live-scores`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Live scores refreshed');
        fetchDashboard();
      } else {
        toast.error('Failed to refresh live scores');
      }
    } catch (err) {
      toast.error('Failed to refresh live scores');
    }
  };

  const [backfilling, setBackfilling] = useState(false);
  
  // League accuracy modal state
  const [selectedLeague, setSelectedLeague] = useState(null);
  const [showLeagueModal, setShowLeagueModal] = useState(false);

  const openLeagueAccuracy = (leagueId) => {
    setSelectedLeague(leagueId);
    setShowLeagueModal(true);
  };

  const triggerAccuracyBackfill = async () => {
    setBackfilling(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/backfill-accuracy`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(`Accuracy backfill complete: ${data.created} created, ${data.updated} updated`);
        fetchDashboard();
      } else {
        toast.error('Failed to run accuracy backfill');
      }
    } catch (err) {
      toast.error('Failed to run accuracy backfill');
    } finally {
      setBackfilling(false);
    }
  };

  // Fetch league visibility settings
  const fetchLeagueVisibility = async () => {
    setLoadingLeagueVisibility(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/league-visibility`, {
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setLeagueVisibility(data);
      }
    } catch (err) {
      console.error('Failed to fetch league visibility:', err);
    } finally {
      setLoadingLeagueVisibility(false);
    }
  };

  // Toggle a league's visibility in accuracy calculation
  const toggleLeagueVisibility = async (leagueId, currentlyEnabled) => {
    setTogglingLeague(leagueId);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/league-visibility/toggle`, {
        method: 'POST',
        credentials: 'include',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          league_id: leagueId,
          enabled: !currentlyEnabled
        })
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchLeagueVisibility();
        fetchDashboard(); // Refresh accuracy data
      } else {
        toast.error('Failed to toggle league visibility');
      }
    } catch (err) {
      toast.error('Failed to toggle league visibility');
    } finally {
      setTogglingLeague(null);
    }
  };

  // Regenerate all predictions using 6-phase system
  const regenerateAllPredictions = async () => {
    setRegeneratingPredictions(true);
    setRegenerationStatus({ status: 'starting' });
    
    try {
      const token = localStorage.getItem('token');
      
      // Start the regeneration task
      const startResponse = await fetch(`${API_URL}/api/admin/regenerate-all-predictions`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!startResponse.ok) {
        toast.error('Failed to start prediction regeneration');
        setRegeneratingPredictions(false);
        return;
      }
      
      toast.info('Started regenerating predictions using 6-phase system...');
      
      // Poll for completion
      let attempts = 0;
      const maxAttempts = 120; // 4 minutes max (polling every 2 seconds)
      
      const pollStatus = async () => {
        while (attempts < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, 2000));
          attempts++;
          
          try {
            const statusResponse = await fetch(`${API_URL}/api/admin/regenerate-predictions-status`, {
              credentials: 'include',
              headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (statusResponse.ok) {
              const status = await statusResponse.json();
              setRegenerationStatus(status);
              
              // Check if completed
              if (!status.running && status.completed_at) {
                if (status.error) {
                  toast.warning(`Completed with errors: ${status.error}`);
                } else {
                  toast.success(`Regenerated ${status.games_processed} predictions!`);
                }
                fetchDashboard();
                return;
              }
            }
          } catch (pollErr) {
            console.error('Error polling regeneration status:', pollErr);
          }
        }
        
        toast.warning('Regeneration taking longer than expected. Check status later.');
      };
      
      await pollStatus();
      
    } catch (err) {
      console.error('Regenerate predictions error:', err);
      toast.error('Failed to regenerate predictions');
    } finally {
      setRegeneratingPredictions(false);
    }
  };

  // Show loading while auth is initializing
  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Navbar />
        <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Skeleton className="h-10 w-64 mb-8" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-32" />
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <Skeleton className="h-96" />
            <Skeleton className="h-96" />
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Show access denied only for auth errors
  if (dashboardError === 'access_denied') {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Navbar />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Shield className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-4">Access Denied</h2>
            <p className="text-muted-foreground mb-4">Admin privileges required</p>
            <Button onClick={() => navigate('/')}>Go Home</Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Show error state for API/network errors
  if (dashboardError || !dashboardData) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Navbar />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-4">Failed to Load Dashboard</h2>
            <p className="text-muted-foreground mb-4">
              {dashboardError === 'network_error' 
                ? 'Network error - please check your connection' 
                : 'Unable to fetch dashboard data from server'}
            </p>
            <div className="flex gap-3 justify-center">
              <Button onClick={() => { setLoading(true); setDashboardError(null); fetchDashboard(); }}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Retry
              </Button>
              <Button variant="outline" onClick={() => navigate('/')}>Go Home</Button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const { users, games, predictions, platform_accuracy, activity, refresh_status = {} } = dashboardData || {};
  
  // Safe defaults to prevent crashes
  const safeAccuracy = platform_accuracy || { overall: 0, correct: 0, total_evaluated: 0, by_league: {} };
  const safePredictions = predictions || { user_predictions: 0, user_accuracy: 0, correct_user_predictions: 0, platform_predictions: 0 };
  const safeUsers = users || { total: 0, subscribers: 0, admins: 0, free_users: 0 };
  const safeGames = games || { total: 0, scheduled: 0, live: 0, final: 0, by_league: {} };
  const safeActivity = activity || { predictions_last_7_days: 0, transactions: 0 };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      
      <main className="flex-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
            <div>
              <h1 className="font-display text-3xl font-bold uppercase tracking-tight flex items-center gap-3" data-testid="admin-dashboard-title">
                <Shield className="w-8 h-8 text-red-400" />
                Admin Dashboard
              </h1>
              <p className="text-muted-foreground mt-1">
                Platform overview and management
              </p>
            </div>
            <div className="flex items-center gap-3 mt-4 md:mt-0">
              {/* Admin/User View Toggle */}
              <div className="flex items-center gap-2 px-3 py-2 bg-card border border-border rounded-sm">
                <UserCog className="w-4 h-4 text-muted-foreground" />
                <Label htmlFor="view-toggle" className="text-sm text-muted-foreground">
                  View as User
                </Label>
                <Switch
                  id="view-toggle"
                  checked={viewAsUser}
                  onCheckedChange={(checked) => {
                    setViewAsUser(checked);
                    if (checked) {
                      navigate('/dashboard');
                    }
                  }}
                />
              </div>
              <Button
                variant="outline"
                className="rounded-sm"
                onClick={handleRefresh}
                disabled={refreshing}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button
                className="rounded-sm"
                onClick={() => navigate('/admin')}
              >
                <Settings className="w-4 h-4 mr-2" />
                Admin Panel
              </Button>
            </div>
          </div>

          {/* Tabs for different admin sections */}
          <Tabs defaultValue="overview" className="space-y-6">
            <TabsList className="bg-card border border-border flex-wrap h-auto gap-1 p-2">
              <TabsTrigger value="overview" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs sm:text-sm">
                <BarChart3 className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                <span className="hidden xs:inline">Overview</span>
                <span className="xs:hidden">Home</span>
              </TabsTrigger>
              <TabsTrigger value="users" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs sm:text-sm" data-testid="users-tab-trigger">
                <Users className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                Users
              </TabsTrigger>
              <TabsTrigger value="revenue" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs sm:text-sm" data-testid="revenue-tab-trigger">
                <DollarSign className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                Revenue
              </TabsTrigger>
              <TabsTrigger value="settings" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs sm:text-sm">
                <Settings className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                Settings
              </TabsTrigger>
              <TabsTrigger value="data-management" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs sm:text-sm">
                <Upload className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Data Management</span>
                <span className="sm:hidden">Data</span>
              </TabsTrigger>
              <TabsTrigger value="prediction-changes" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs sm:text-sm">
                <History className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Prediction Changes</span>
                <span className="sm:hidden">Changes</span>
              </TabsTrigger>
              <TabsTrigger value="learning-analytics" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs sm:text-sm" data-testid="ai-learning-tab">
                <Brain className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">AI Learning</span>
                <span className="sm:hidden">AI</span>
              </TabsTrigger>
              <TabsTrigger value="bug-scanner" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs sm:text-sm">
                <Bug className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Bug Scanner</span>
                <span className="sm:hidden">Bugs</span>
              </TabsTrigger>
              <TabsTrigger value="messages" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs sm:text-sm" data-testid="messages-tab-trigger">
                <MessageCircle className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Messages</span>
                <span className="sm:hidden">Msgs</span>
              </TabsTrigger>
              <TabsTrigger value="system-health" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs sm:text-sm" data-testid="system-health-tab">
                <Activity className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">System Health</span>
                <span className="sm:hidden">Health</span>
              </TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-6">
          {/* Prominent Data Fix Banner - Easy to find */}
          <Card className="bg-gradient-to-r from-green-500/20 via-yellow-500/20 to-orange-500/20 border-2 border-green-500/50" data-testid="data-fix-banner">
            <CardContent className="py-5">
              <div className="flex flex-col gap-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-500/30 rounded-full">
                    <Shield className="w-6 h-6 text-green-400" />
                  </div>
                  <div>
                    <h3 className="font-bold text-green-400 text-lg">Data Integrity Tools</h3>
                    <p className="text-sm text-muted-foreground">One-click maintenance: Refresh games, fix alignments, backfill accuracy</p>
                  </div>
                </div>
                
                {/* Primary: Run All Fixes Button */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button 
                    size="lg"
                    className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-bold shadow-lg shadow-green-500/25 flex-1 sm:flex-none"
                    onClick={runAllFixes}
                    disabled={runningAllFixes || fixingPredictions || triggeringRefresh}
                    data-testid="run-all-fixes-btn"
                  >
                    {runningAllFixes ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Running All Fixes...
                      </>
                    ) : (
                      <>
                        <Shield className="w-5 h-5 mr-2" />
                        Run All Fixes (Recommended)
                      </>
                    )}
                  </Button>
                  
                  {/* Individual Buttons */}
                  <div className="flex flex-wrap gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/20"
                      onClick={fixPredictionAlignment}
                      disabled={fixingPredictions || runningAllFixes}
                      data-testid="fix-predictions-btn-banner"
                    >
                      {fixingPredictions ? (
                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      ) : (
                        <Target className="w-4 h-4 mr-1" />
                      )}
                      Fix Alignments
                    </Button>
                    <Button 
                      variant="outline"
                      size="sm"
                      className="border-blue-500/50 text-blue-400 hover:bg-blue-500/20"
                      onClick={triggerDataRefresh}
                      disabled={triggeringRefresh || runningAllFixes}
                    >
                      {triggeringRefresh ? (
                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4 mr-1" />
                      )}
                      Refresh
                    </Button>
                    <Button 
                      variant="outline"
                      size="sm"
                      className="border-purple-500/50 text-purple-400 hover:bg-purple-500/20"
                      onClick={triggerAccuracyBackfill}
                      disabled={backfilling || runningAllFixes}
                    >
                      {backfilling ? (
                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      ) : (
                        <BarChart3 className="w-4 h-4 mr-1" />
                      )}
                      Backfill
                    </Button>
                    <Button 
                      variant="outline"
                      size="sm"
                      className="border-orange-500/50 text-orange-400 hover:bg-orange-500/20"
                      onClick={regenerateAllPredictions}
                      disabled={regeneratingPredictions || runningAllFixes}
                      title="Regenerate all predictions using the full 6-phase AI system"
                    >
                      {regeneratingPredictions ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                          {regenerationStatus?.games_processed || 0}/{regenerationStatus?.total_games || '?'}
                        </>
                      ) : (
                        <>
                          <Brain className="w-4 h-4 mr-1" />
                          Regen 6-Phase
                        </>
                      )}
                    </Button>
                  </div>
                </div>
                
                {/* Last Run Indicator */}
                {lastRunAllFixes && lastRunAllFixes.has_run && (
                  <div className="mt-4 p-3 bg-slate-800/50 rounded-lg border border-slate-700/50">
                    <div className="flex items-center gap-2 text-sm">
                      <Clock className="w-4 h-4 text-slate-400" />
                      <span className="text-slate-400">Last run:</span>
                      <span className={`font-medium ${lastRunAllFixes.success ? 'text-green-400' : 'text-yellow-400'}`}>
                        {new Date(lastRunAllFixes.timestamp).toLocaleString()}
                      </span>
                      {lastRunAllFixes.success ? (
                        <Badge className="bg-green-500/20 text-green-400 text-xs ml-2">
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          Success
                        </Badge>
                      ) : (
                        <Badge className="bg-yellow-500/20 text-yellow-400 text-xs ml-2">
                          <AlertTriangle className="w-3 h-3 mr-1" />
                          With Errors
                        </Badge>
                      )}
                    </div>
                    {lastRunAllFixes.results_summary && (
                      <div className="mt-2 flex flex-wrap gap-2 text-xs text-slate-400">
                        {lastRunAllFixes.results_summary.predictions_corrected > 0 && (
                          <span className="bg-slate-700/50 px-2 py-1 rounded">
                            {lastRunAllFixes.results_summary.predictions_corrected} predictions corrected
                          </span>
                        )}
                        {lastRunAllFixes.results_summary.games_locked > 0 && (
                          <span className="bg-slate-700/50 px-2 py-1 rounded">
                            {lastRunAllFixes.results_summary.games_locked} games locked
                          </span>
                        )}
                        {lastRunAllFixes.results_summary.scores_generated > 0 && (
                          <span className="bg-slate-700/50 px-2 py-1 rounded">
                            {lastRunAllFixes.results_summary.scores_generated} scores generated
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                )}
                {!lastRunAllFixes?.has_run && !loadingLastRun && (
                  <div className="mt-4 p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/30">
                    <div className="flex items-center gap-2 text-sm text-yellow-400">
                      <AlertTriangle className="w-4 h-4" />
                      <span>No fix has been run yet. Click "Run All Fixes" to ensure data integrity.</span>
                    </div>
                  </div>
                )}
              </div>
              </CardContent>
            </Card>

            {/* Daily Scheduler Status Banner - 1 AM MST Automatic Updates */}
            <Card className={`border-2 ${
              dailySchedulerStatus?.last_run_status === 'success' 
                ? 'border-green-500/50 bg-green-500/10' 
                : dailySchedulerStatus?.last_run_status === 'failed' || dailySchedulerStatus?.last_run_status === 'error'
                  ? 'border-red-500/50 bg-red-500/10'
                  : 'border-blue-500/50 bg-blue-500/10'
            }`} data-testid="daily-scheduler-status">
              <CardContent className="py-5">
                <div className="flex flex-col gap-4">
                  {/* Header */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-full ${
                        dailySchedulerStatus?.last_run_status === 'success' 
                          ? 'bg-green-500/30' 
                          : dailySchedulerStatus?.last_run_status === 'failed' || dailySchedulerStatus?.last_run_status === 'error'
                            ? 'bg-red-500/30'
                            : 'bg-blue-500/30'
                      }`}>
                        <Clock className={`w-6 h-6 ${
                          dailySchedulerStatus?.last_run_status === 'success' 
                            ? 'text-green-400' 
                            : dailySchedulerStatus?.last_run_status === 'failed' || dailySchedulerStatus?.last_run_status === 'error'
                              ? 'text-red-400'
                              : 'text-blue-400'
                        }`} />
                      </div>
                      <div>
                        <h3 className={`font-bold text-lg ${
                          dailySchedulerStatus?.last_run_status === 'success' 
                            ? 'text-green-400' 
                            : dailySchedulerStatus?.last_run_status === 'failed' || dailySchedulerStatus?.last_run_status === 'error'
                              ? 'text-red-400'
                              : 'text-blue-400'
                        }`}>
                          Daily Auto-Update (1 AM MST)
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          Automatic predictions, AI learning & schedule updates
                        </p>
                      </div>
                    </div>
                    
                    {/* Status Badge */}
                    {dailySchedulerStatus && (
                      <Badge className={`text-sm px-3 py-1 ${
                        dailySchedulerStatus.last_run_status === 'success'
                          ? 'bg-green-500/20 text-green-400 border-green-500/30'
                          : dailySchedulerStatus.last_run_status === 'failed' || dailySchedulerStatus.last_run_status === 'error'
                            ? 'bg-red-500/20 text-red-400 border-red-500/30'
                            : 'bg-blue-500/20 text-blue-400 border-blue-500/30'
                      }`}>
                        {dailySchedulerStatus.last_run_status === 'success' ? (
                          <><CheckCircle2 className="w-4 h-4 mr-1" /> Success</>
                        ) : dailySchedulerStatus.last_run_status === 'failed' || dailySchedulerStatus.last_run_status === 'error' ? (
                          <><AlertCircle className="w-4 h-4 mr-1" /> Failed</>
                        ) : (
                          <><Clock className="w-4 h-4 mr-1" /> Pending</>
                        )}
                      </Badge>
                    )}
                  </div>

                  {/* Loading State */}
                  {loadingSchedulerStatus && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Loading scheduler status...</span>
                    </div>
                  )}

                  {/* Status Details */}
                  {dailySchedulerStatus && !loadingSchedulerStatus && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      {/* Last Run Time */}
                      <div className="p-3 bg-slate-800/50 rounded-lg border border-slate-700/50">
                        <div className="flex items-center gap-2 mb-1">
                          <Clock className="w-4 h-4 text-slate-400" />
                          <span className="text-xs font-medium text-slate-400">Last Run</span>
                        </div>
                        <p className="text-sm font-medium text-foreground">
                          {dailySchedulerStatus.last_run_time 
                            ? new Date(dailySchedulerStatus.last_run_time).toLocaleString()
                            : 'Never'}
                        </p>
                      </div>

                      {/* AI Learning */}
                      <div className={`p-3 rounded-lg border ${
                        dailySchedulerStatus.results?.ai_learning_processed > 0 
                          ? 'bg-blue-500/10 border-blue-500/30' 
                          : 'bg-slate-800/50 border-slate-700/50'
                      }`}>
                        <div className="flex items-center gap-2 mb-1">
                          <Brain className={`w-4 h-4 ${dailySchedulerStatus.results?.ai_learning_processed > 0 ? 'text-blue-400' : 'text-slate-400'}`} />
                          <span className="text-xs font-medium">AI Learning</span>
                        </div>
                        <p className="text-lg font-bold text-foreground">
                          {dailySchedulerStatus.results?.ai_learning_processed || 0}
                        </p>
                        <p className="text-xs text-muted-foreground">games analyzed</p>
                      </div>

                      {/* AI Predictions */}
                      <div className={`p-3 rounded-lg border ${
                        dailySchedulerStatus.results?.ai_predictions_2day > 0 
                          ? 'bg-orange-500/10 border-orange-500/30' 
                          : 'bg-slate-800/50 border-slate-700/50'
                      }`}>
                        <div className="flex items-center gap-2 mb-1">
                          <Sparkles className={`w-4 h-4 ${dailySchedulerStatus.results?.ai_predictions_2day > 0 ? 'text-orange-400' : 'text-slate-400'}`} />
                          <span className="text-xs font-medium">AI Predictions</span>
                        </div>
                        <p className="text-lg font-bold text-foreground">
                          {dailySchedulerStatus.results?.ai_predictions_2day || 0}
                        </p>
                        <p className="text-xs text-muted-foreground">new predictions</p>
                      </div>

                      {/* Platform Accuracy */}
                      <div className={`p-3 rounded-lg border ${
                        dailySchedulerStatus.results?.platform_accuracy > 50 
                          ? 'bg-green-500/10 border-green-500/30' 
                          : 'bg-slate-800/50 border-slate-700/50'
                      }`}>
                        <div className="flex items-center gap-2 mb-1">
                          <Target className={`w-4 h-4 ${dailySchedulerStatus.results?.platform_accuracy > 50 ? 'text-green-400' : 'text-slate-400'}`} />
                          <span className="text-xs font-medium">Platform Accuracy</span>
                        </div>
                        <p className="text-lg font-bold text-foreground">
                          {dailySchedulerStatus.results?.platform_accuracy?.toFixed(1) || '—'}%
                        </p>
                        <p className="text-xs text-muted-foreground">overall accuracy</p>
                      </div>
                    </div>
                  )}

                  {/* Failed/Pending Status - Show Retry Button */}
                  {dailySchedulerStatus && (
                    dailySchedulerStatus.last_run_status === 'failed' || 
                    dailySchedulerStatus.last_run_status === 'error' ||
                    dailySchedulerStatus.last_run_status === 'partial' ||
                    dailySchedulerStatus.last_run_status === 'never_run' ||
                    dailySchedulerStatus.last_run_status === 'running' ||
                    (dailySchedulerStatus.last_run_time === null && dailySchedulerStatus.results?.ai_learning_processed === 0)
                  ) && (
                    <div className={`mt-2 p-3 rounded-lg border ${
                      dailySchedulerStatus.last_run_status === 'running' 
                        ? 'bg-yellow-500/10 border-yellow-500/30'
                        : 'bg-red-500/10 border-red-500/30'
                    }`}>
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className={`flex items-center gap-2 text-sm ${
                          dailySchedulerStatus.last_run_status === 'running' ? 'text-yellow-400' : 'text-red-400'
                        }`}>
                          <AlertCircle className="w-4 h-4" />
                          <span>
                            {dailySchedulerStatus.last_run_status === 'running' 
                              ? 'A previous update appears stuck. Click retry to run maintenance tasks safely.'
                              : dailySchedulerStatus.last_run_status === 'never_run' || dailySchedulerStatus.last_run_time === null
                                ? 'No scheduler data found. Click retry to run maintenance tasks manually.'
                                : 'Last automatic update failed or incomplete. Click retry to run maintenance tasks manually.'}
                          </span>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className={`${
                            dailySchedulerStatus.last_run_status === 'running'
                              ? 'border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/20'
                              : 'border-red-500/50 text-red-400 hover:bg-red-500/20'
                          }`}
                          onClick={retryDailyScheduler}
                          disabled={autoRetrying}
                        >
                          {autoRetrying ? (
                            <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Retrying...</>
                          ) : (
                            <><RefreshCw className="w-4 h-4 mr-1" /> Retry Now</>
                          )}
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Success Message */}
                  {dailySchedulerStatus && dailySchedulerStatus.last_run_status === 'success' && (
                    <div className="text-xs text-green-400/80 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" />
                      <span>All prediction systems running smoothly. Next update at 1:00 AM MST.</span>
                    </div>
                  )}

                  {/* Refresh Button */}
                  <div className="flex justify-end">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-muted-foreground hover:text-foreground"
                      onClick={fetchDailySchedulerStatus}
                      disabled={loadingSchedulerStatus}
                    >
                      {loadingSchedulerStatus ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4" />
                      )}
                      <span className="ml-1">Refresh Status</span>
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* AI Learning & Phased Prediction Monitoring - From Last Manual Run */}
            {lastRunAllFixes && lastRunAllFixes.has_run && lastRunAllFixes.results_summary && (
              <Card className="border-slate-700/50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <History className="w-4 h-4 text-slate-400" />
                    Last Manual Run Details
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                    {/* Refresh Status */}
                    <div className={`p-3 rounded-lg border ${lastRunAllFixes.results_summary.refresh_games?.status === 'success' ? 'bg-green-500/10 border-green-500/30' : 'bg-slate-800/50 border-slate-700/50'}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <RefreshCw className={`w-4 h-4 ${lastRunAllFixes.results_summary.refresh_games?.status === 'success' ? 'text-green-400' : 'text-slate-400'}`} />
                        <span className="text-xs font-medium">ESPN Refresh</span>
                      </div>
                      <p className="text-lg font-bold text-foreground">
                        {lastRunAllFixes.results_summary.refresh_games?.total_games || 'Done'}
                      </p>
                      <p className="text-xs text-muted-foreground">games fetched</p>
                    </div>
                    
                    {/* Alignment Status */}
                    <div className={`p-3 rounded-lg border ${(lastRunAllFixes.results_summary.score_alignment?.games_fixed > 0 || lastRunAllFixes.results_summary.comprehensive_alignment?.games_fixed > 0) ? 'bg-yellow-500/10 border-yellow-500/30' : 'bg-green-500/10 border-green-500/30'}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <Target className={`w-4 h-4 ${(lastRunAllFixes.results_summary.score_alignment?.games_fixed > 0 || lastRunAllFixes.results_summary.comprehensive_alignment?.games_fixed > 0) ? 'text-yellow-400' : 'text-green-400'}`} />
                        <span className="text-xs font-medium">Fix Alignment</span>
                      </div>
                      <p className="text-lg font-bold text-foreground">
                        {(lastRunAllFixes.results_summary.score_alignment?.games_fixed || 0) + (lastRunAllFixes.results_summary.comprehensive_alignment?.games_fixed || 0)}
                      </p>
                      <p className="text-xs text-muted-foreground">games aligned</p>
                    </div>
                    
                    {/* Backfill Status */}
                    <div className={`p-3 rounded-lg border ${lastRunAllFixes.results_summary.backfill_accuracy?.status === 'success' ? 'bg-purple-500/10 border-purple-500/30' : 'bg-slate-800/50 border-slate-700/50'}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <BarChart3 className={`w-4 h-4 ${lastRunAllFixes.results_summary.backfill_accuracy?.status === 'success' ? 'text-purple-400' : 'text-slate-400'}`} />
                        <span className="text-xs font-medium">Backfill</span>
                      </div>
                      <p className="text-lg font-bold text-foreground">
                        {lastRunAllFixes.results_summary.backfill_accuracy?.accuracy || '—'}%
                      </p>
                      <p className="text-xs text-muted-foreground">platform accuracy</p>
                    </div>
                    
                    {/* Regen 6-Phase / AI Predictions */}
                    <div className={`p-3 rounded-lg border ${lastRunAllFixes.results_summary.generate_predictions?.games_generated > 0 ? 'bg-orange-500/10 border-orange-500/30' : 'bg-green-500/10 border-green-500/30'}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <Brain className={`w-4 h-4 ${lastRunAllFixes.results_summary.generate_predictions?.games_generated > 0 ? 'text-orange-400' : 'text-green-400'}`} />
                        <span className="text-xs font-medium">AI Predictions</span>
                      </div>
                      <p className="text-lg font-bold text-foreground">
                        {lastRunAllFixes.results_summary.generate_predictions?.games_generated || 0}
                      </p>
                      <p className="text-xs text-muted-foreground">new predictions</p>
                    </div>
                    
                    {/* AI Learning Status */}
                    <div className={`p-3 rounded-lg border ${lastRunAllFixes.results_summary.ai_learning_processed > 0 ? 'bg-blue-500/10 border-blue-500/30' : 'bg-slate-800/50 border-slate-700/50'}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <Sparkles className={`w-4 h-4 ${lastRunAllFixes.results_summary.ai_learning_processed > 0 ? 'text-blue-400' : 'text-slate-400'}`} />
                        <span className="text-xs font-medium">AI Learning</span>
                      </div>
                      <p className="text-lg font-bold text-foreground">
                        {lastRunAllFixes.results_summary.ai_learning_processed || '—'}
                      </p>
                      <p className="text-xs text-muted-foreground">games learned</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          
          {/* Top Stats Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {/* Total Users */}
            <Card data-testid="stat-total-users">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Total Users
                </CardTitle>
                <Users className="w-4 h-4 text-blue-400" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold font-mono">{safeUsers.total}</div>
                <div className="flex items-center gap-2 mt-2">
                  <Badge className="bg-green-500/20 text-green-400 text-xs">
                    {safeUsers.subscribers} subscribers
                  </Badge>
                  <Badge className="bg-red-500/20 text-red-400 text-xs">
                    {safeUsers.admins} admins
                  </Badge>
                </div>
              </CardContent>
            </Card>

            {/* Total Games */}
            <Card data-testid="stat-total-games">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Total Games
                </CardTitle>
                <Calendar className="w-4 h-4 text-purple-400" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold font-mono">{safeGames.total}</div>
                <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Radio className="w-3 h-3 text-red-400 animate-pulse" />
                    {safeGames.live} live
                  </span>
                  <span>|</span>
                  <span>{safeGames.scheduled} scheduled</span>
                  <span>|</span>
                  <span>{safeGames.final} final</span>
                </div>
              </CardContent>
            </Card>

            {/* Platform Accuracy */}
            <Card data-testid="stat-platform-accuracy">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Platform Accuracy
                </CardTitle>
                <TrendingUp className="w-4 h-4 text-green-400" />
              </CardHeader>
              <CardContent>
                <div className={`text-3xl font-bold font-mono ${
                  safeAccuracy.overall >= 60 ? 'text-green-400' : 
                  safeAccuracy.overall >= 40 ? 'text-yellow-400' : 'text-red-400'
                }`}>
                  {safeAccuracy.overall}%
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  {safeAccuracy.correct}/{safeAccuracy.total_evaluated} correct
                </p>
              </CardContent>
            </Card>

            {/* User Predictions */}
            <Card data-testid="stat-user-predictions">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  User Predictions
                </CardTitle>
                <Target className="w-4 h-4 text-yellow-400" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold font-mono">{safePredictions.user_predictions}</div>
                <p className="text-xs text-muted-foreground mt-2">
                  {safePredictions.user_accuracy}% accuracy ({safePredictions.correct_user_predictions} correct)
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Games by League & Accuracy */}
            <div className="lg:col-span-2 space-y-8">
              {/* Games by League */}
              <Card data-testid="games-by-league-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-purple-400" />
                    Games by League
                  </CardTitle>
                  <CardDescription>Distribution of games across leagues</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {Object.entries(safeGames.by_league || {}).map(([league, count]) => (
                      <div key={league}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium">{league}</span>
                          <span className="text-sm font-mono">{count}</span>
                        </div>
                        <Progress 
                          value={(count / safeGames.total) * 100} 
                          className="h-2"
                        />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* League Visibility Settings */}
              <Card data-testid="league-visibility-card" className="border-primary/20">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Settings className="w-5 h-5 text-primary" />
                        League Accuracy Settings
                      </CardTitle>
                      <CardDescription>Toggle which leagues are included in platform accuracy calculation</CardDescription>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={fetchLeagueVisibility}
                      disabled={loadingLeagueVisibility}
                      className="rounded-sm"
                    >
                      {loadingLeagueVisibility ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {leagueVisibility ? (
                    <div className="space-y-4">
                      {/* MLB Spring Training Note */}
                      <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-sm">
                        <div className="flex items-center gap-2 mb-1">
                          <AlertTriangle className="w-4 h-4 text-amber-400" />
                          <span className="text-sm font-medium text-amber-400">MLB Spring Training</span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          MLB games during Spring Training (Feb-Mar) are excluded from accuracy calculation regardless of toggle status
                        </p>
                      </div>
                      
                      {/* League Toggles */}
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                        {Object.entries(leagueVisibility.leagues || {}).map(([leagueId, data]) => (
                          <div 
                            key={leagueId}
                            className={`p-3 rounded-sm border ${data.enabled ? 'bg-green-500/10 border-green-500/30' : 'bg-secondary/30 border-border'}`}
                          >
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium uppercase">{leagueId}</span>
                              <button
                                onClick={() => toggleLeagueVisibility(leagueId, data.enabled)}
                                disabled={togglingLeague === leagueId}
                                className={`relative w-10 h-5 rounded-full transition-colors ${data.enabled ? 'bg-green-500' : 'bg-muted'}`}
                                data-testid={`toggle-${leagueId}`}
                              >
                                {togglingLeague === leagueId ? (
                                  <Loader2 className="w-3 h-3 animate-spin absolute top-1 left-1/2 -translate-x-1/2" />
                                ) : (
                                  <span 
                                    className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${data.enabled ? 'translate-x-5' : 'translate-x-0.5'}`}
                                  />
                                )}
                              </button>
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">
                              {data.enabled ? 'Included' : 'Excluded'}
                            </p>
                          </div>
                        ))}
                      </div>
                      
                      <p className="text-xs text-muted-foreground text-center pt-2 border-t border-border">
                        Changes take effect immediately. Excluded leagues still appear on game board.
                      </p>
                    </div>
                  ) : loadingLeagueVisibility ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="w-6 h-6 animate-spin text-primary" />
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-center py-4">
                      Failed to load league settings
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Accuracy by League */}
              <Card data-testid="accuracy-by-league-card">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-green-400" />
                        Platform Accuracy by League
                      </CardTitle>
                      <CardDescription>Prediction accuracy breakdown</CardDescription>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={triggerAccuracyBackfill}
                      disabled={backfilling}
                      className="rounded-sm"
                      data-testid="backfill-accuracy-btn"
                    >
                      {backfilling ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4 mr-2" />
                      )}
                      Recalculate
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {safeAccuracy.by_league && Object.keys(safeAccuracy.by_league).length > 0 ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {Object.entries(safeAccuracy.by_league).map(([league, data]) => (
                        <button 
                          key={league}
                          onClick={() => openLeagueAccuracy(league)}
                          className="p-4 bg-secondary/30 rounded-sm text-center hover:bg-secondary/50 transition-colors cursor-pointer group relative"
                          data-testid={`league-accuracy-${league}`}
                        >
                          {/* MLB Spring Training Badge */}
                          {league === 'mlb' && data.total === 0 && (
                            <div className="absolute -top-2 -right-2 bg-amber-500/20 text-amber-400 text-[10px] px-2 py-0.5 rounded-full border border-amber-500/30">
                              Spring Training
                            </div>
                          )}
                          <div className="flex items-center justify-between mb-1">
                            <p className="text-xs text-muted-foreground uppercase">{league.toUpperCase()}</p>
                            <ChevronRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                          {league === 'mlb' && data.total === 0 ? (
                            <>
                              <p className="text-lg font-medium text-amber-400">
                                Starts Apr '26
                              </p>
                              <p className="text-xs text-muted-foreground mt-1">
                                Click for details
                              </p>
                            </>
                          ) : (
                            <>
                              <p className={`text-2xl font-bold font-mono ${
                                data.accuracy >= 60 ? 'text-green-400' : 
                                data.accuracy >= 40 ? 'text-yellow-400' : 'text-red-400'
                              }`}>
                                {data.accuracy}%
                              </p>
                              <p className="text-xs text-muted-foreground mt-1">
                                {data.correct}/{data.total}
                              </p>
                            </>
                          )}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-center py-4">
                      No accuracy data available yet
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Recent Activity */}
              <Card data-testid="recent-activity-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="w-5 h-5 text-blue-400" />
                    Recent User Activity
                  </CardTitle>
                  <CardDescription>Latest user predictions</CardDescription>
                </CardHeader>
                <CardContent>
                  {recentActivity && recentActivity.activities && recentActivity.activities.length > 0 ? (
                    <div className="space-y-3">
                      {recentActivity.activities.slice(0, 8).map((activity, idx) => (
                        <div 
                          key={idx}
                          className="flex items-center justify-between p-3 bg-secondary/30 rounded-sm"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center">
                              <Target className="w-4 h-4 text-purple-400" />
                            </div>
                            <div>
                              <p className="text-sm font-medium">{activity.user_email}</p>
                              <p className="text-xs text-muted-foreground">
                                Predicted {activity.predicted_winner} in {activity.league}
                              </p>
                            </div>
                          </div>
                          <Badge 
                            variant="outline" 
                            className={
                              activity.status === 'correct' ? 'text-green-400' :
                              activity.status === 'incorrect' ? 'text-red-400' :
                              'text-yellow-400'
                            }
                          >
                            {activity.status}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-center py-8">
                      No recent activity
                    </p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Right Column - System Status & Actions */}
            <div className="space-y-6">
              {/* System Status */}
              <Card data-testid="system-status-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Database className="w-5 h-5 text-green-400" />
                    System Status
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-secondary/30 rounded-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                      <span className="text-sm">API Status</span>
                    </div>
                    <Badge className="bg-green-500/20 text-green-400">Online</Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-secondary/30 rounded-sm">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm">Last Refresh</span>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {refresh_status?.last_espn_refresh 
                        ? new Date(refresh_status.last_espn_refresh).toLocaleTimeString()
                        : 'Never'
                      }
                    </span>
                  </div>
                  {refresh_status?.api_version && (
                    <div className="flex items-center justify-between p-3 bg-green-500/10 rounded-sm">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-400" />
                        <span className="text-sm">API Version</span>
                      </div>
                      <span className="text-xs font-mono text-green-400">
                        {refresh_status.api_version}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center justify-between p-3 bg-secondary/30 rounded-sm">
                    <div className="flex items-center gap-2">
                      <Activity className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm">Predictions (7d)</span>
                    </div>
                    <span className="font-mono">{safeActivity.predictions_last_7_days}</span>
                  </div>
                </CardContent>
              </Card>

              {/* Integration Status */}
              <Card data-testid="integration-status-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Settings className="w-5 h-5 text-blue-400" />
                    Integration Status
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-secondary/30 rounded-sm">
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm">Stripe</span>
                    </div>
                    {integrationStatus ? (
                      <Badge className={integrationStatus.stripe?.configured 
                        ? "bg-green-500/20 text-green-400" 
                        : "bg-red-500/20 text-red-400"}>
                        {integrationStatus.stripe?.configured 
                          ? `Configured (${integrationStatus.stripe.type})` 
                          : "Not Configured"}
                      </Badge>
                    ) : (
                      <Skeleton className="h-5 w-24" />
                    )}
                  </div>
                  <div className="flex items-center justify-between p-3 bg-secondary/30 rounded-sm">
                    <div className="flex items-center gap-2">
                      <Activity className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm">Resend (Email)</span>
                    </div>
                    {integrationStatus ? (
                      <Badge className={integrationStatus.resend?.configured 
                        ? "bg-green-500/20 text-green-400" 
                        : "bg-red-500/20 text-red-400"}>
                        {integrationStatus.resend?.configured ? "Configured" : "Not Configured"}
                      </Badge>
                    ) : (
                      <Skeleton className="h-5 w-24" />
                    )}
                  </div>
                  {integrationStatus?.sender_email && (
                    <div className="pt-2 border-t border-border">
                      <p className="text-xs text-muted-foreground">
                        Sender: {integrationStatus.sender_email}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* All League Standings */}
              <Card data-testid="all-standings-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="w-5 h-5 text-yellow-400" />
                    League Standings
                  </CardTitle>
                  <CardDescription>All sports by win percentage</CardDescription>
                </CardHeader>
                <CardContent>
                  {loadingStandings ? (
                    <div className="space-y-2">
                      {[1,2,3,4,5].map(i => <Skeleton key={i} className="h-8" />)}
                    </div>
                  ) : allStandings ? (
                    <div className="space-y-4 max-h-96 overflow-y-auto">
                      {Object.entries(allStandings).map(([leagueId, league]) => (
                        league.standings?.length > 0 && (
                          <div key={leagueId} className="space-y-1">
                            <h4 className="text-sm font-bold text-muted-foreground uppercase tracking-wide">
                              {league.league_name}
                            </h4>
                            {league.standings.slice(0, 5).map((team, idx) => (
                              <div key={team.team_id} className="flex items-center justify-between text-sm hover:bg-secondary/30 rounded-sm px-1 py-0.5">
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-muted-foreground w-4">{idx + 1}</span>
                                  <span className="truncate">{team.abbreviation}</span>
                                </div>
                                <div className="flex items-center gap-3">
                                  <span className="font-mono text-xs">{team.wins}-{team.losses}</span>
                                  <span className="font-mono text-green-400">{team.win_pct}%</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-4">No standings available</p>
                  )}
                </CardContent>
              </Card>

              {/* Quick Actions - Simplified */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Quick Actions
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <Button 
                    size="sm"
                    className="w-full justify-start rounded-sm bg-gradient-to-r from-green-500/20 to-emerald-500/20 text-green-400 hover:from-green-500/30 hover:to-emerald-500/30 border border-green-500/30"
                    onClick={runAllFixes}
                    disabled={runningAllFixes}
                    data-testid="run-all-fixes-sidebar"
                  >
                    {runningAllFixes ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Shield className="w-4 h-4 mr-2" />
                    )}
                    Run All Fixes
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="w-full justify-start rounded-sm"
                    onClick={triggerLiveScoreRefresh}
                  >
                    <Radio className="w-4 h-4 mr-2" />
                    Refresh Live Scores
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="w-full justify-start rounded-sm"
                    onClick={() => navigate('/admin')}
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Manage Settings
                  </Button>
                </CardContent>
              </Card>

              {/* Platform Predictions Summary */}
              <Card className="bg-gradient-to-br from-purple-500/10 to-blue-500/10 border-purple-500/20">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="w-5 h-5 text-purple-400" />
                    Platform Predictions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center">
                    <p className="text-4xl font-bold font-mono text-purple-400">
                      {safePredictions.platform_predictions}
                    </p>
                    <p className="text-sm text-muted-foreground mt-2">
                      AI-generated predictions
                    </p>
                  </div>
                  <div className="mt-4 pt-4 border-t border-border">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">User Predictions</span>
                      <span className="font-mono">{safePredictions.user_predictions}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm mt-2">
                      <span className="text-muted-foreground">User Accuracy</span>
                      <span className="font-mono">{safePredictions.user_accuracy}%</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Pricing Management */}
              <Card className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border-green-500/20" data-testid="pricing-management">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-green-400" />
                    Pricing Management
                  </CardTitle>
                  <CardDescription>Configure subscription and analysis pricing</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="subscription-price" className="text-sm">
                      Monthly Subscription (CAD)
                    </Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                      <Input
                        id="subscription-price"
                        type="number"
                        step="0.01"
                        min="0.50"
                        value={pricingForm.subscription_price}
                        onChange={(e) => setPricingForm({...pricingForm, subscription_price: parseFloat(e.target.value) || 0})}
                        className="pl-8 rounded-sm"
                        data-testid="subscription-price-input"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="analysis-price" className="text-sm">
                      AI Analysis Price (CAD)
                    </Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                      <Input
                        id="analysis-price"
                        type="number"
                        step="0.01"
                        min="0.50"
                        value={pricingForm.ai_analysis_price}
                        onChange={(e) => setPricingForm({...pricingForm, ai_analysis_price: parseFloat(e.target.value) || 0})}
                        className="pl-8 rounded-sm"
                        data-testid="analysis-price-input"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="referrer-credit" className="text-sm">
                        Referrer Credit (%)
                      </Label>
                      <Input
                        id="referrer-credit"
                        type="number"
                        min="0"
                        max="100"
                        value={pricingForm.referrer_credit_percent}
                        onChange={(e) => setPricingForm({...pricingForm, referrer_credit_percent: parseFloat(e.target.value) || 0})}
                        className="rounded-sm"
                        data-testid="referrer-credit-input"
                      />
                      <p className="text-xs text-muted-foreground">Credit earned by existing user</p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="referee-credit" className="text-sm">
                        Referee Credit (%)
                      </Label>
                      <Input
                        id="referee-credit"
                        type="number"
                        min="0"
                        max="100"
                        value={pricingForm.referee_credit_percent}
                        onChange={(e) => setPricingForm({...pricingForm, referee_credit_percent: parseFloat(e.target.value) || 0})}
                        className="rounded-sm"
                        data-testid="referee-credit-input"
                      />
                      <p className="text-xs text-muted-foreground">Discount for new user who signs up</p>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="trial-days" className="text-sm">
                      Free Trial Period (Days)
                    </Label>
                    <Input
                      id="trial-days"
                      type="number"
                      min="0"
                      max="30"
                      value={pricingForm.trial_days}
                      onChange={(e) => setPricingForm({...pricingForm, trial_days: parseInt(e.target.value) || 0})}
                      className="rounded-sm"
                      data-testid="trial-days-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      New users get {pricingForm.trial_days} days free before being charged
                    </p>
                  </div>
                  
                  {pricing && (
                    <div className="text-xs text-muted-foreground pt-2 border-t border-border space-y-1">
                      <p>Referrer Credit: ${Number(pricingForm.subscription_price * pricingForm.referrer_credit_percent / 100 || 0).toFixed(2)} CAD</p>
                      <p>Referee Discount: ${Number(pricingForm.subscription_price * pricingForm.referee_credit_percent / 100 || 0).toFixed(2)} CAD</p>
                    </div>
                  )}
                  
                  <Button 
                    onClick={savePricing} 
                    disabled={savingPricing}
                    className="w-full btn-blaze rounded-sm"
                    data-testid="save-pricing-btn"
                  >
                    {savingPricing ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    Save Pricing
                  </Button>
                </CardContent>
              </Card>

              {/* Alerts/Warnings */}
              {safeGames.live > 0 && (
                <Card className="bg-red-500/10 border-red-500/20">
                  <CardContent className="py-4">
                    <div className="flex items-center gap-3">
                      <Radio className="w-5 h-5 text-red-400 animate-pulse" />
                      <div>
                        <p className="font-medium text-red-400">Live Games Active</p>
                        <p className="text-sm text-muted-foreground">
                          {safeGames.live} game{safeGames.live > 1 ? 's' : ''} currently in progress
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
            </TabsContent>

            {/* Users Tab - User Management */}
            <TabsContent value="users" className="space-y-6" data-testid="users-tab-content">
              {/* Search and Filter Bar */}
              <div className="flex flex-col md:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by email or name..."
                    value={usersSearch}
                    onChange={(e) => setUsersSearch(e.target.value)}
                    className="pl-10 rounded-sm"
                    data-testid="users-search-input"
                  />
                </div>
                <Select value={usersFilter} onValueChange={setUsersFilter}>
                  <SelectTrigger className="w-[180px]" data-testid="users-filter-select">
                    <SelectValue placeholder="Filter users" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Users</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="subscribers">Subscribers</SelectItem>
                    <SelectItem value="non_subscribers">Non-Subscribers</SelectItem>
                    <SelectItem value="admins">Admins</SelectItem>
                  </SelectContent>
                </Select>
                <Button 
                  variant="outline" 
                  onClick={() => fetchUsers(usersPagination.page, usersSearch, usersFilter)}
                  disabled={usersLoading}
                  className="rounded-sm"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${usersLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>

              {/* User Stats Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="bg-blue-500/10 border-blue-500/20">
                  <CardContent className="py-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-blue-400">{usersPagination.total}</p>
                      <p className="text-xs text-muted-foreground">Total Users</p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-green-500/10 border-green-500/20">
                  <CardContent className="py-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-green-400">
                        {usersList.filter(u => u.is_subscriber).length}
                      </p>
                      <p className="text-xs text-muted-foreground">Subscribers (Page)</p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-purple-500/10 border-purple-500/20">
                  <CardContent className="py-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-purple-400">
                        {usersList.filter(u => u.is_admin).length}
                      </p>
                      <p className="text-xs text-muted-foreground">Admins (Page)</p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-red-500/10 border-red-500/20">
                  <CardContent className="py-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-red-400">
                        {usersList.filter(u => !u.is_active).length}
                      </p>
                      <p className="text-xs text-muted-foreground">Inactive (Page)</p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Users Table */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-blue-400" />
                    User Management
                  </CardTitle>
                  <CardDescription>
                    Click on a user to view detailed profile. Page {usersPagination.page} of {usersPagination.total_pages}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {usersLoading ? (
                    <div className="space-y-3">
                      {[1,2,3,4,5].map(i => <Skeleton key={i} className="h-14" />)}
                    </div>
                  ) : usersList.length === 0 ? (
                    <p className="text-muted-foreground text-center py-8">No users found</p>
                  ) : (
                    <div className="space-y-2">
                      {usersList.map((u) => (
                        <div 
                          key={u.user_id}
                          className="flex flex-col md:flex-row md:items-center justify-between p-3 bg-secondary/30 rounded-sm hover:bg-secondary/50 transition-colors group gap-3"
                          data-testid={`user-row-${u.user_id}`}
                        >
                          {/* User Info */}
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                              !u.is_active ? 'bg-red-500/20' : 
                              u.is_admin ? 'bg-purple-500/20' : 
                              u.is_subscriber ? 'bg-green-500/20' : 'bg-blue-500/20'
                            }`}>
                              {u.is_admin ? (
                                <Shield className="w-5 h-5 text-purple-400" />
                              ) : !u.is_active ? (
                                <UserX className="w-5 h-5 text-red-400" />
                              ) : (
                                <UserCheck className="w-5 h-5 text-green-400" />
                              )}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="font-medium truncate">{u.name || 'Unnamed User'}</p>
                              <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                            </div>
                          </div>
                          
                          {/* Badges */}
                          <div className="flex items-center gap-2 flex-wrap pl-13 md:pl-0">
                            {u.is_admin && <Badge className="bg-purple-500/20 text-purple-400 text-xs">Admin</Badge>}
                            {u.is_subscriber && <Badge className="bg-green-500/20 text-green-400 text-xs">Subscriber</Badge>}
                            {u.has_free_account && <Badge className="bg-yellow-500/20 text-yellow-400 text-xs">Free</Badge>}
                            {!u.is_active && <Badge className="bg-red-500/20 text-red-400 text-xs">Inactive</Badge>}
                            <span className="text-xs text-muted-foreground hidden lg:inline">
                              {u.created_at ? new Date(u.created_at).toLocaleDateString() : 'N/A'}
                            </span>
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-2 pl-13 md:pl-0" onClick={(e) => e.stopPropagation()}>
                            {/* Free Account Toggle */}
                            <div className="flex items-center gap-1.5 px-2 py-1 bg-card rounded-sm border border-border">
                              <Switch
                                id={`free-${u.user_id}`}
                                checked={u.has_free_account || false}
                                onCheckedChange={() => toggleFreeAccount(u.user_id)}
                                disabled={togglingFreeAccount === u.user_id}
                                data-testid={`toggle-free-${u.user_id}`}
                                className="scale-75"
                              />
                              <Label htmlFor={`free-${u.user_id}`} className="text-xs text-muted-foreground cursor-pointer whitespace-nowrap">
                                {u.has_free_account ? 'Free ✓' : 'Grant Free'}
                              </Label>
                            </div>
                            
                            {/* View Details Button */}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => fetchUserDetail(u.user_id)}
                              className="rounded-sm"
                              data-testid={`view-user-${u.user_id}`}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              <span className="hidden sm:inline">View</span>
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Pagination */}
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                    <p className="text-sm text-muted-foreground">
                      Showing {(usersPagination.page - 1) * usersPagination.per_page + 1} - {Math.min(usersPagination.page * usersPagination.per_page, usersPagination.total)} of {usersPagination.total}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fetchUsers(usersPagination.page - 1, usersSearch, usersFilter)}
                        disabled={!usersPagination.has_prev || usersLoading}
                        className="rounded-sm"
                        data-testid="users-prev-btn"
                      >
                        <ChevronLeft className="w-4 h-4 mr-1" />
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fetchUsers(usersPagination.page + 1, usersSearch, usersFilter)}
                        disabled={!usersPagination.has_next || usersLoading}
                        className="rounded-sm"
                        data-testid="users-next-btn"
                      >
                        Next
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Revenue Dashboard Tab */}
            <TabsContent value="revenue" className="space-y-6" data-testid="revenue-tab-content">
              {revenueLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : revenueData ? (
                <>
                  {/* Revenue Stats */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card className="bg-green-500/10 border-green-500/20">
                      <CardContent className="py-4">
                        <div className="text-center">
                          <p className="text-2xl font-bold text-green-400">${revenueData.stats.total_revenue}</p>
                          <p className="text-xs text-muted-foreground">Total Revenue</p>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="bg-blue-500/10 border-blue-500/20">
                      <CardContent className="py-4">
                        <div className="text-center">
                          <p className="text-2xl font-bold text-blue-400">${revenueData.stats.monthly_revenue}</p>
                          <p className="text-xs text-muted-foreground">This Month</p>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="bg-purple-500/10 border-purple-500/20">
                      <CardContent className="py-4">
                        <div className="text-center">
                          <p className="text-2xl font-bold text-purple-400">{revenueData.stats.total_subscribers}</p>
                          <p className="text-xs text-muted-foreground">Subscribers</p>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="bg-amber-500/10 border-amber-500/20">
                      <CardContent className="py-4">
                        <div className="text-center">
                          <p className="text-2xl font-bold text-amber-400">{revenueData.stats.conversion_rate}%</p>
                          <p className="text-xs text-muted-foreground">Conversion Rate</p>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Subscribers List */}
                    <Card className="bg-card/50 border-border/50">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <UserCheck className="w-5 h-5 text-green-400" />
                          Active Subscribers ({revenueData.subscribers?.length || 0})
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2 max-h-96 overflow-y-auto">
                          {revenueData.subscribers?.map((sub, idx) => (
                            <div key={idx} className="p-3 bg-slate-800/50 rounded-lg flex justify-between items-center">
                              <div>
                                <p className="font-medium">{sub.name || 'Unknown'}</p>
                                <p className="text-xs text-muted-foreground">{sub.email}</p>
                              </div>
                              <div className="text-right">
                                {sub.subscription_end && (
                                  <Badge variant={
                                    Math.ceil((new Date(sub.subscription_end) - new Date()) / (1000 * 60 * 60 * 24)) <= 7 
                                      ? "destructive" : "default"
                                  }>
                                    {Math.max(0, Math.ceil((new Date(sub.subscription_end) - new Date()) / (1000 * 60 * 60 * 24)))} days left
                                  </Badge>
                                )}
                              </div>
                            </div>
                          ))}
                          {(!revenueData.subscribers || revenueData.subscribers.length === 0) && (
                            <p className="text-center text-muted-foreground py-4">No subscribers yet</p>
                          )}
                        </div>
                      </CardContent>
                    </Card>

                    {/* Bulk Email to Non-Subscribers */}
                    <Card className="bg-card/50 border-border/50">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Mail className="w-5 h-5 text-primary" />
                          Marketing Email Campaign
                        </CardTitle>
                        <CardDescription>
                          Send promotional emails to {revenueData.stats.total_non_subscribers} non-subscribers
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div>
                          <Label>Email Subject</Label>
                          <Input 
                            value={bulkEmailSubject}
                            onChange={(e) => setBulkEmailSubject(e.target.value)}
                            placeholder="Special Offer from SportsAnalysisAB!"
                          />
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Discount % (0 = none)</Label>
                            <Input 
                              type="number"
                              min="0"
                              max="100"
                              value={bulkEmailDiscount}
                              onChange={(e) => setBulkEmailDiscount(Number(e.target.value))}
                            />
                          </div>
                          <div>
                            <Label>Free Trial Days (0 = none)</Label>
                            <Input 
                              type="number"
                              min="0"
                              max="30"
                              value={bulkEmailTrialDays}
                              onChange={(e) => setBulkEmailTrialDays(Number(e.target.value))}
                            />
                          </div>
                        </div>

                        <div>
                          <Label>Custom Message (optional)</Label>
                          <Input 
                            value={bulkEmailMessage}
                            onChange={(e) => setBulkEmailMessage(e.target.value)}
                            placeholder="Add a personal message..."
                          />
                        </div>

                        <div className="bg-slate-800/50 p-3 rounded-lg">
                          <p className="text-sm text-muted-foreground mb-2">Preview Offer:</p>
                          <ul className="text-sm space-y-1">
                            {bulkEmailDiscount > 0 && (
                              <li className="text-green-400">✓ {bulkEmailDiscount}% discount</li>
                            )}
                            {bulkEmailTrialDays > 0 && (
                              <li className="text-blue-400">✓ {bulkEmailTrialDays} days free trial</li>
                            )}
                            {bulkEmailDiscount === 0 && bulkEmailTrialDays === 0 && (
                              <li className="text-muted-foreground">No special offer configured</li>
                            )}
                          </ul>
                        </div>

                        <Button 
                          onClick={sendBulkMarketingEmail}
                          disabled={sendingBulkEmail}
                          className="w-full"
                          data-testid="send-bulk-email-btn"
                        >
                          {sendingBulkEmail ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Sending...
                            </>
                          ) : (
                            <>
                              <Send className="w-4 h-4 mr-2" />
                              Send to All Non-Subscribers ({revenueData.stats.total_non_subscribers})
                            </>
                          )}
                        </Button>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Recent Transactions */}
                  <Card className="bg-card/50 border-border/50">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <DollarSign className="w-5 h-5 text-green-400" />
                        Recent Transactions
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {revenueData.recent_transactions?.map((txn, idx) => (
                          <div key={idx} className="p-3 bg-slate-800/50 rounded-lg flex justify-between items-center">
                            <div>
                              <p className="text-sm font-mono">{txn.transaction_id || txn.session_id?.slice(0, 20)}</p>
                              <p className="text-xs text-muted-foreground">{txn.created_at ? new Date(txn.created_at).toLocaleString() : 'N/A'}</p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-green-400">${Number(txn.amount || 0).toFixed(2)}</p>
                              <Badge variant={txn.status === 'completed' ? 'default' : 'secondary'}>
                                {txn.status}
                              </Badge>
                            </div>
                          </div>
                        ))}
                        {(!revenueData.recent_transactions || revenueData.recent_transactions.length === 0) && (
                          <p className="text-center text-muted-foreground py-4">No transactions yet</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </>
              ) : (
                <Card className="bg-card/50">
                  <CardContent className="py-12 text-center">
                    <p className="text-muted-foreground">Failed to load revenue data</p>
                    <Button onClick={fetchRevenueData} variant="outline" className="mt-4">
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Retry
                    </Button>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Settings Tab - Pricing & Referral Management */}
            <TabsContent value="settings" className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Pricing Management */}
                <Card className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border-green-500/20" data-testid="pricing-management-settings">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <DollarSign className="w-5 h-5 text-green-400" />
                      Pricing & Subscription
                    </CardTitle>
                    <CardDescription>Configure subscription pricing and free trial</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="settings-subscription-price" className="text-sm font-medium">
                        Monthly Subscription Price (CAD)
                      </Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                        <Input
                          id="settings-subscription-price"
                          type="number"
                          step="0.01"
                          min="0.50"
                          value={pricingForm.subscription_price}
                          onChange={(e) => setPricingForm({...pricingForm, subscription_price: parseFloat(e.target.value) || 0})}
                          className="pl-8 rounded-sm"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="settings-analysis-price" className="text-sm font-medium">
                        AI Analysis Price (CAD)
                      </Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                        <Input
                          id="settings-analysis-price"
                          type="number"
                          step="0.01"
                          min="0.50"
                          value={pricingForm.ai_analysis_price}
                          onChange={(e) => setPricingForm({...pricingForm, ai_analysis_price: parseFloat(e.target.value) || 0})}
                          className="pl-8 rounded-sm"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="settings-trial-days" className="text-sm font-medium">
                        Free Trial Period (Days)
                      </Label>
                      <Input
                        id="settings-trial-days"
                        type="number"
                        min="0"
                        max="30"
                        value={pricingForm.trial_days}
                        onChange={(e) => setPricingForm({...pricingForm, trial_days: parseInt(e.target.value) || 0})}
                        className="rounded-sm"
                      />
                      <p className="text-xs text-muted-foreground">
                        New users get {pricingForm.trial_days} days free access before being charged
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {/* Referral Program */}
                <Card className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border-purple-500/20" data-testid="referral-management-settings">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Gift className="w-5 h-5 text-purple-400" />
                      Referral Program
                    </CardTitle>
                    <CardDescription>Configure referral rewards for referrers and new users</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="settings-referrer-credit" className="text-sm font-medium">
                        Referrer Credit (%)
                      </Label>
                      <Input
                        id="settings-referrer-credit"
                        type="number"
                        min="0"
                        max="100"
                        value={pricingForm.referrer_credit_percent}
                        onChange={(e) => setPricingForm({...pricingForm, referrer_credit_percent: parseFloat(e.target.value) || 0})}
                        className="rounded-sm"
                      />
                      <p className="text-xs text-muted-foreground">
                        Credit earned by existing user when someone uses their referral code
                      </p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="settings-referee-credit" className="text-sm font-medium">
                        Referee Discount (%)
                      </Label>
                      <Input
                        id="settings-referee-credit"
                        type="number"
                        min="0"
                        max="100"
                        value={pricingForm.referee_credit_percent}
                        onChange={(e) => setPricingForm({...pricingForm, referee_credit_percent: parseFloat(e.target.value) || 0})}
                        className="rounded-sm"
                      />
                      <p className="text-xs text-muted-foreground">
                        Discount for new user who signs up with a referral code
                      </p>
                    </div>
                    
                    <div className="bg-secondary/30 p-4 rounded-sm mt-4">
                      <h4 className="text-sm font-medium mb-2">Current Referral Summary</h4>
                      <ul className="text-xs text-muted-foreground space-y-1">
                        <li>• Existing users earn {pricingForm.referrer_credit_percent}% credit per referral</li>
                        <li>• New users get {pricingForm.referee_credit_percent}% discount on first subscription</li>
                        <li>• Free trial: {pricingForm.trial_days} days before subscription charges</li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </div>
              
              {/* Save Button */}
              <div className="flex justify-end">
                <Button
                  onClick={savePricing}
                  disabled={savingPricing}
                  className="btn-blaze rounded-sm px-8"
                  data-testid="save-settings-btn"
                >
                  {savingPricing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Settings
                    </>
                  )}
                </Button>
              </div>
            </TabsContent>

            {/* Data Management Tab */}
            <TabsContent value="data-management" className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Upload Form */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Upload className="w-5 h-5 text-blue-400" />
                      Upload Game Data
                    </CardTitle>
                    <CardDescription>
                      Upload games via JSON or CSV file
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Upload Type Selection */}
                    <div className="flex gap-4">
                      <Button
                        variant={uploadType === 'json' ? 'default' : 'outline'}
                        onClick={() => setUploadType('json')}
                        className="flex-1"
                      >
                        <FileJson className="w-4 h-4 mr-2" />
                        JSON
                      </Button>
                      <Button
                        variant={uploadType === 'csv' ? 'default' : 'outline'}
                        onClick={() => setUploadType('csv')}
                        className="flex-1"
                      >
                        <FileSpreadsheet className="w-4 h-4 mr-2" />
                        CSV
                      </Button>
                    </div>

                    {/* JSON Upload */}
                    {uploadType === 'json' && (
                      <div className="space-y-3">
                        <Label>JSON Data</Label>
                        <textarea
                          className="w-full h-48 p-3 bg-background border border-border rounded-sm font-mono text-sm resize-none"
                          placeholder={`[
  {
    "home_team_id": "nhl_tor",
    "away_team_id": "nhl_mtl",
    "league_id": "nhl",
    "start_time": "2025-12-25T19:00:00Z",
    "venue": "Scotiabank Arena",
    "home_probability": 55,
    "away_probability": 45,
    "confidence": "Medium"
  }
]`}
                          value={jsonData}
                          onChange={(e) => setJsonData(e.target.value)}
                          data-testid="json-upload-textarea"
                        />
                        <Button
                          onClick={handleJsonUpload}
                          disabled={uploading || !jsonData.trim()}
                          className="w-full"
                          data-testid="json-upload-btn"
                        >
                          {uploading ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Upload className="w-4 h-4 mr-2" />
                          )}
                          Upload JSON Data
                        </Button>
                      </div>
                    )}

                    {/* CSV Upload */}
                    {uploadType === 'csv' && (
                      <div className="space-y-3">
                        <Label>CSV File</Label>
                        <div className="border-2 border-dashed border-border rounded-sm p-6 text-center">
                          <input
                            type="file"
                            accept=".csv"
                            onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
                            className="hidden"
                            id="csv-upload"
                            data-testid="csv-file-input"
                          />
                          <label htmlFor="csv-upload" className="cursor-pointer">
                            <FileSpreadsheet className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
                            <p className="text-sm text-muted-foreground">
                              {csvFile ? csvFile.name : 'Click to select CSV file'}
                            </p>
                          </label>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Required columns: home_team_id, away_team_id, league_id, start_time
                        </p>
                        <Button
                          onClick={handleCsvUpload}
                          disabled={uploading || !csvFile}
                          className="w-full"
                          data-testid="csv-upload-btn"
                        >
                          {uploading ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Upload className="w-4 h-4 mr-2" />
                          )}
                          Upload CSV File
                        </Button>
                      </div>
                    )}

                    {/* Upload Result */}
                    {uploadResult && (
                      <div className={`p-3 rounded-sm ${uploadResult.error ? 'bg-red-500/10 border border-red-500/30' : 'bg-green-500/10 border border-green-500/30'}`}>
                        {uploadResult.error ? (
                          <p className="text-sm text-red-400">{uploadResult.error}</p>
                        ) : (
                          <div className="text-sm text-green-400">
                            <p>Uploaded: {uploadResult.successful || uploadResult.uploaded} games</p>
                            {uploadResult.errors?.length > 0 && (
                              <div className="mt-2 text-yellow-400">
                                <p>Warnings:</p>
                                <ul className="list-disc list-inside text-xs">
                                  {uploadResult.errors.slice(0, 3).map((err, i) => (
                                    <li key={i}>{err}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Upload Instructions */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Database className="w-5 h-5 text-purple-400" />
                      Upload Instructions
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-3">
                      <h4 className="font-medium text-sm">Required Fields:</h4>
                      <ul className="text-sm text-muted-foreground space-y-1">
                        <li><code className="bg-card px-1 rounded">home_team_id</code> - e.g., "nhl_tor", "nba_lal"</li>
                        <li><code className="bg-card px-1 rounded">away_team_id</code> - e.g., "nhl_mtl", "nba_bos"</li>
                        <li><code className="bg-card px-1 rounded">league_id</code> - nhl, nba, nfl, mlb</li>
                        <li><code className="bg-card px-1 rounded">start_time</code> - ISO 8601 format</li>
                      </ul>
                    </div>
                    <div className="space-y-3">
                      <h4 className="font-medium text-sm">Optional Fields:</h4>
                      <ul className="text-sm text-muted-foreground space-y-1">
                        <li><code className="bg-card px-1 rounded">venue</code> - Arena/Stadium name</li>
                        <li><code className="bg-card px-1 rounded">home_probability</code> - 0-100</li>
                        <li><code className="bg-card px-1 rounded">away_probability</code> - 0-100</li>
                        <li><code className="bg-card px-1 rounded">confidence</code> - High/Medium/Low</li>
                      </ul>
                    </div>
                    <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-sm">
                      <p className="text-xs text-yellow-400">
                        Team IDs should match existing teams in the database. Use format: league_abbreviation (e.g., nhl_tor for Toronto Maple Leafs)
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Prediction Changes Tab */}
            <TabsContent value="prediction-changes" className="space-y-6">
              {/* Summary Stats */}
              {predictionChangesSummary && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="pt-4">
                      <div className="text-2xl font-bold font-mono text-blue-400">
                        {predictionChangesSummary.shift_statistics?.total_changes || 0}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">Changes (Last 7 Days)</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4">
                      <div className="flex items-center gap-2 text-2xl font-bold font-mono">
                        {predictionChangesSummary.shift_statistics?.avg_home_shift > 0 ? (
                          <TrendingUp className="w-5 h-5 text-green-400" />
                        ) : (
                          <TrendingDown className="w-5 h-5 text-red-400" />
                        )}
                        <span className={predictionChangesSummary.shift_statistics?.avg_home_shift > 0 ? 'text-green-400' : 'text-red-400'}>
                          {predictionChangesSummary.shift_statistics?.avg_home_shift > 0 ? '+' : ''}
                          {predictionChangesSummary.shift_statistics?.avg_home_shift || 0}%
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">Avg Home Prob Shift</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4">
                      <div className="text-2xl font-bold font-mono text-purple-400">
                        {Object.keys(predictionChangesSummary.by_league || {}).length}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">Leagues Active</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4">
                      <div className="text-2xl font-bold font-mono text-amber-400">
                        {predictionChangesSummary.by_trigger_source?.scheduler || 0}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">Auto-Updates (Scheduler)</p>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Filters */}
              <Card>
                <CardContent className="pt-4">
                  <div className="flex flex-wrap gap-4 items-center">
                    <div className="flex items-center gap-2">
                      <Label className="text-sm">League:</Label>
                      <Select
                        value={predictionChangesFilter.league_id}
                        onValueChange={(value) => setPredictionChangesFilter({...predictionChangesFilter, league_id: value})}
                      >
                        <SelectTrigger className="w-32">
                          <SelectValue placeholder="All" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Leagues</SelectItem>
                          <SelectItem value="nhl">NHL</SelectItem>
                          <SelectItem value="nba">NBA</SelectItem>
                          <SelectItem value="nfl">NFL</SelectItem>
                          <SelectItem value="mlb">MLB</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-2">
                      <Label className="text-sm">Type:</Label>
                      <Select
                        value={predictionChangesFilter.change_type}
                        onValueChange={(value) => setPredictionChangesFilter({...predictionChangesFilter, change_type: value})}
                      >
                        <SelectTrigger className="w-40">
                          <SelectValue placeholder="All" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Types</SelectItem>
                          <SelectItem value="winner_flip">Winner Flips Only</SelectItem>
                          <SelectItem value="2hr_pregame_recalculation">2-Hour Pregame</SelectItem>
                          <SelectItem value="initial">Initial</SelectItem>
                          <SelectItem value="pregame_update">Pre-Game Update</SelectItem>
                          <SelectItem value="statistical_update">Statistical Update</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={fetchPredictionChanges}
                      disabled={predictionChangesLoading}
                    >
                      <RefreshCw className={`w-4 h-4 mr-1 ${predictionChangesLoading ? 'animate-spin' : ''}`} />
                      Refresh
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Changes List */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <History className="w-5 h-5 text-blue-400" />
                    Prediction Change Log
                  </CardTitle>
                  <CardDescription>
                    Track all prediction updates with timestamps and explanations. Winner flips are highlighted in red.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {predictionChangesLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : predictionChanges.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <History className="w-10 h-10 mx-auto mb-3 opacity-50" />
                      <p>No prediction changes recorded yet</p>
                      <p className="text-xs mt-1">Changes will appear here as predictions are updated</p>
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-[600px] overflow-y-auto">
                      {predictionChanges.map((change, idx) => (
                        <div 
                          key={change.change_id || idx}
                          className={`p-4 border rounded-sm hover:border-primary/50 transition-colors cursor-pointer ${
                            change.winner_flipped 
                              ? 'bg-red-500/10 border-red-500/30 hover:border-red-500/50' 
                              : 'bg-card border-border'
                          }`}
                          onClick={() => fetchGameChanges(change.game_id)}
                          data-testid={`prediction-change-${idx}`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                {change.winner_flipped && (
                                  <Badge className="bg-red-500 text-white text-xs animate-pulse">
                                    WINNER FLIP
                                  </Badge>
                                )}
                                <Badge className={`text-xs ${
                                  change.change_type === 'initial' ? 'bg-green-500/20 text-green-400' :
                                  change.change_type === '2hr_pregame_recalculation' ? 'bg-orange-500/20 text-orange-400' :
                                  change.change_type === 'pregame_update' ? 'bg-blue-500/20 text-blue-400' :
                                  'bg-purple-500/20 text-purple-400'
                                }`}>
                                  {change.change_type?.replace(/_/g, ' ')}
                                </Badge>
                                <Badge variant="outline" className="text-xs">
                                  {change.league_id?.toUpperCase()}
                                </Badge>
                                <span className="text-xs text-muted-foreground flex items-center gap-1">
                                  <ClockIcon className="w-3 h-3" />
                                  {new Date(change.timestamp || change.created_at).toLocaleString()}
                                </span>
                              </div>
                              <p className="font-medium text-sm">
                                {change.away_team} @ {change.home_team}
                              </p>
                              {change.winner_flipped ? (
                                <div className="flex items-center gap-2 mt-2 text-sm">
                                  <span className="text-red-400 line-through">{change.old_winner}</span>
                                  <ArrowRight className="w-4 h-4 text-muted-foreground" />
                                  <span className="text-green-400 font-bold">{change.new_winner}</span>
                                </div>
                              ) : (
                                <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                                  {change.reason || change.change_reason}
                                </p>
                              )}
                            </div>
                            <div className="flex flex-col items-end gap-1 ml-4">
                              <div className="flex items-center gap-2 text-sm">
                                <span className="text-muted-foreground">
                                  {Number(change.old_values?.home_probability || change.old_home_probability || 0).toFixed(0)}%
                                </span>
                                <ArrowRight className="w-3 h-3 text-muted-foreground" />
                                <span className={`font-mono font-bold ${
                                  (change.probability_change?.home_delta || (change.new_home_probability - change.old_home_probability)) > 0 ? 'text-green-400' : 
                                  (change.probability_change?.home_delta || (change.new_home_probability - change.old_home_probability)) < 0 ? 'text-red-400' : 
                                  'text-muted-foreground'
                                }`}>
                                  {Number(change.new_values?.home_probability || change.new_home_probability || 0).toFixed(0)}%
                                </span>
                              </div>
                              {change.probability_change !== undefined && change.probability_change !== 0 && (
                                <span className={`text-xs ${
                                  change.probability_change > 0 ? 'text-green-400' : 'text-red-400'
                                }`}>
                                  ({Number(change.probability_change || 0) > 0 ? '+' : ''}{Number(change.probability_change || 0).toFixed(1)}%)
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Learning Analytics Tab */}
            <TabsContent value="learning-analytics" className="space-y-6">
              <AILearningDashboard />
            </TabsContent>

            {/* Bug Scanner Tab */}
            <TabsContent value="bug-scanner" className="space-y-6">
              <BugScanner />
            </TabsContent>

            {/* Messages Tab */}
            <TabsContent value="messages" className="space-y-6" data-testid="messages-tab-content">
              <AdminMessages />
            </TabsContent>

            {/* System Health Tab */}
            <TabsContent value="system-health" className="space-y-6" data-testid="system-health-tab-content">
              <SystemHealthDashboard />
            </TabsContent>
          </Tabs>
        </div>
      </main>

      <Footer />
      
      {/* League Accuracy Detail Modal */}
      <LeagueAccuracyModal
        isOpen={showLeagueModal}
        onClose={() => setShowLeagueModal(false)}
        leagueId={selectedLeague}
        leagueName={selectedLeague?.toUpperCase()}
        isPlatform={true}
      />

      {/* Game Prediction History Modal */}
      <Dialog open={showGameChangesModal} onOpenChange={setShowGameChangesModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-blue-400" />
              Prediction History
            </DialogTitle>
            <DialogDescription>
              {selectedGameChanges && (
                <span>
                  {selectedGameChanges.away_team} @ {selectedGameChanges.home_team} ({selectedGameChanges.league_id?.toUpperCase()})
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          {selectedGameChanges && (
            <div className="space-y-4">
              {/* Current Prediction */}
              <Card className="bg-primary/5 border-primary/20">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Current Prediction</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Win Probability</p>
                      <p className="font-mono font-bold">
                        Home: {Number(selectedGameChanges.current_prediction?.home_probability || 0).toFixed(1)}% | 
                        Away: {Number(selectedGameChanges.current_prediction?.away_probability || 0).toFixed(1)}%
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Predicted Score</p>
                      <p className="font-mono font-bold">
                        {selectedGameChanges.current_prediction?.platform_home_score} - {selectedGameChanges.current_prediction?.platform_away_score}
                      </p>
                    </div>
                  </div>
                  {selectedGameChanges.current_prediction?.prediction_locked && (
                    <Badge className="mt-2 bg-red-500/20 text-red-400">Prediction Locked</Badge>
                  )}
                </CardContent>
              </Card>

              {/* Change Timeline */}
              <div className="space-y-3">
                <h4 className="font-medium text-sm flex items-center gap-2">
                  <ClockIcon className="w-4 h-4" />
                  Change Timeline ({selectedGameChanges.total_changes} changes)
                </h4>
                {selectedGameChanges.changes?.map((change, idx) => (
                  <div key={idx} className="pl-4 border-l-2 border-border pb-4">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge className={`text-xs ${
                        change.change_type === 'initial' ? 'bg-green-500/20 text-green-400' :
                        change.change_type === 'pregame_update' ? 'bg-blue-500/20 text-blue-400' :
                        'bg-purple-500/20 text-purple-400'
                      }`}>
                        {change.change_type?.replace(/_/g, ' ')}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {new Date(change.timestamp).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-sm mt-1">{change.reason}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs">
                      <span className="text-muted-foreground">
                        {Number(change.old_values?.home_probability || 0).toFixed(0)}%
                      </span>
                      <ArrowRight className="w-3 h-3 text-muted-foreground" />
                      <span className={`font-mono font-bold ${
                        Number(change.probability_change?.home_delta || 0) > 0 ? 'text-green-400' : 
                        Number(change.probability_change?.home_delta || 0) < 0 ? 'text-red-400' : ''
                      }`}>
                        {Number(change.new_values?.home_probability || 0).toFixed(0)}% 
                        ({Number(change.probability_change?.home_delta || 0) > 0 ? '+' : ''}{Number(change.probability_change?.home_delta || 0).toFixed(1)}%)
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* User Detail Modal */}
      <Dialog open={showUserModal} onOpenChange={setShowUserModal}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="user-detail-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCog className="w-5 h-5 text-blue-400" />
              User Profile
            </DialogTitle>
            <DialogDescription>
              {selectedUser && (
                <span>{selectedUser.email}</span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          {userDetailLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-20" />
              <Skeleton className="h-32" />
              <Skeleton className="h-32" />
            </div>
          ) : !selectedUser ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>Loading user data...</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* User Header */}
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                    !selectedUser.is_active ? 'bg-red-500/20' : 
                    selectedUser.is_admin ? 'bg-purple-500/20' : 
                    selectedUser.is_subscriber ? 'bg-green-500/20' : 'bg-blue-500/20'
                  }`}>
                    {selectedUser.is_admin ? (
                      <Shield className="w-8 h-8 text-purple-400" />
                    ) : !selectedUser.is_active ? (
                      <UserX className="w-8 h-8 text-red-400" />
                    ) : (
                      <UserCheck className="w-8 h-8 text-green-400" />
                    )}
                  </div>
                  <div>
                    <h3 className="text-lg font-bold">{selectedUser.name || 'Unnamed User'}</h3>
                    <p className="text-sm text-muted-foreground">{selectedUser.email}</p>
                    <div className="flex gap-2 mt-2">
                      {selectedUser.is_admin && <Badge className="bg-purple-500/20 text-purple-400">Admin</Badge>}
                      {selectedUser.is_subscriber && <Badge className="bg-green-500/20 text-green-400">Subscriber</Badge>}
                      {selectedUser.has_free_account && <Badge className="bg-yellow-500/20 text-yellow-400">Free Account</Badge>}
                      {!selectedUser.is_active && <Badge className="bg-red-500/20 text-red-400">Inactive</Badge>}
                    </div>
                  </div>
                </div>
                <Button
                  variant={selectedUser.is_active ? "destructive" : "default"}
                  size="sm"
                  onClick={() => toggleUserActive(selectedUser.user_id)}
                  disabled={togglingUserStatus === selectedUser.user_id}
                  data-testid="toggle-user-active-btn"
                >
                  {togglingUserStatus === selectedUser.user_id ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : selectedUser.is_active ? (
                    <UserX className="w-4 h-4 mr-2" />
                  ) : (
                    <UserCheck className="w-4 h-4 mr-2" />
                  )}
                  {selectedUser.is_active ? 'Deactivate' : 'Activate'}
                </Button>
              </div>

              {/* Basic Info */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Account Information</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">User ID</p>
                      <p className="font-mono text-xs">{selectedUser.user_id?.slice(0, 20)}...</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Joined</p>
                      <p>{selectedUser.created_at ? new Date(selectedUser.created_at).toLocaleDateString() : 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Last Login</p>
                      <p>{selectedUser.last_login ? new Date(selectedUser.last_login).toLocaleString() : 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Email Verified</p>
                      <p className={selectedUser.email_verified ? 'text-green-400' : 'text-red-400'}>
                        {selectedUser.email_verified ? 'Yes' : 'No'}
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">2FA Enabled</p>
                      <p className={selectedUser.totp_enabled ? 'text-green-400' : 'text-muted-foreground'}>
                        {selectedUser.totp_enabled ? 'Yes' : 'No'}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Prediction Stats */}
              <Card className="bg-blue-500/5 border-blue-500/20">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Target className="w-4 h-4 text-blue-400" />
                    Prediction Stats
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-2xl font-bold text-blue-400">{selectedUser.prediction_stats?.total || 0}</p>
                      <p className="text-xs text-muted-foreground">Total</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-green-400">{selectedUser.prediction_stats?.correct || 0}</p>
                      <p className="text-xs text-muted-foreground">Correct</p>
                    </div>
                    <div>
                      <p className={`text-2xl font-bold ${
                        selectedUser.prediction_stats?.accuracy >= 60 ? 'text-green-400' : 
                        selectedUser.prediction_stats?.accuracy >= 40 ? 'text-yellow-400' : 'text-red-400'
                      }`}>{selectedUser.prediction_stats?.accuracy || 0}%</p>
                      <p className="text-xs text-muted-foreground">Accuracy</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Subscription Info */}
              <Card className="bg-green-500/5 border-green-500/20">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-green-400" />
                    Subscription
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Status</p>
                      <p className={`font-medium ${
                        selectedUser.subscription_info?.status === 'active' ? 'text-green-400' :
                        selectedUser.subscription_info?.status === 'trialing' ? 'text-blue-400' :
                        selectedUser.subscription_info?.status === 'free_grant' ? 'text-yellow-400' :
                        selectedUser.subscription_info?.status === 'canceled' ? 'text-red-400' : ''
                      }`}>
                        {selectedUser.subscription_info?.status === 'free_grant' ? 'Free Account' : 
                         selectedUser.subscription_info?.status || 'None'}
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Type</p>
                      <p>{selectedUser.subscription_info?.type || 'N/A'}</p>
                    </div>
                    {selectedUser.subscription_info?.subscription_start && (
                      <div>
                        <p className="text-muted-foreground">Started</p>
                        <p>{new Date(selectedUser.subscription_info.subscription_start).toLocaleDateString()}</p>
                      </div>
                    )}
                    {selectedUser.subscription_info?.subscription_end && (
                      <>
                        <div>
                          <p className="text-muted-foreground">Ends</p>
                          <p>{new Date(selectedUser.subscription_info.subscription_end).toLocaleDateString()}</p>
                        </div>
                        <div className="col-span-2 mt-2">
                          <p className="text-muted-foreground">Days Remaining</p>
                          <p className={`font-bold ${
                            Math.ceil((new Date(selectedUser.subscription_info.subscription_end) - new Date()) / (1000 * 60 * 60 * 24)) <= 7 
                              ? 'text-red-400' 
                              : 'text-green-400'
                          }`}>
                            {Math.max(0, Math.ceil((new Date(selectedUser.subscription_info.subscription_end) - new Date()) / (1000 * 60 * 60 * 24)))} days
                          </p>
                        </div>
                      </>
                    )}
                    {/* Trial Information */}
                    {selectedUser.subscription_info?.status === 'trialing' && (
                      <>
                        {selectedUser.subscription_info?.trial_start && (
                          <div>
                            <p className="text-muted-foreground">Trial Started</p>
                            <p>{new Date(selectedUser.subscription_info.trial_start).toLocaleDateString()}</p>
                          </div>
                        )}
                        {selectedUser.subscription_info?.trial_end && (
                          <div>
                            <p className="text-muted-foreground">Trial Ends</p>
                            <p>{new Date(selectedUser.subscription_info.trial_end).toLocaleDateString()}</p>
                          </div>
                        )}
                        <div className="col-span-2 mt-2">
                          <p className="text-muted-foreground">Trial Days Remaining</p>
                          <p className={`font-bold ${
                            (selectedUser.subscription_info?.trial_days_remaining || 0) <= 3 
                              ? 'text-red-400' 
                              : 'text-blue-400'
                          }`}>
                            {selectedUser.subscription_info?.trial_days_remaining ?? 
                              (selectedUser.subscription_info?.trial_end ? 
                                Math.max(0, Math.ceil((new Date(selectedUser.subscription_info.trial_end) - new Date()) / (1000 * 60 * 60 * 24))) 
                                : 'N/A'
                              )} days
                          </p>
                        </div>
                      </>
                    )}
                    {!selectedUser.subscription_info?.subscription_start && 
                     !selectedUser.subscription_info?.trial_start && 
                     selectedUser.is_subscriber && (
                      <div className="col-span-2 text-center py-2">
                        <p className="text-yellow-400 text-sm">Legacy subscriber - no date info available</p>
                      </div>
                    )}
                  </div>
                  
                  {/* Free Account Toggle */}
                  <div className="mt-4 pt-4 border-t border-border">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium">Free Account Access</p>
                        <p className="text-xs text-muted-foreground">Grant or revoke free subscriber access</p>
                      </div>
                      <Button
                        variant={selectedUser.has_free_account ? "destructive" : "default"}
                        size="sm"
                        onClick={() => toggleFreeAccount(selectedUser.user_id)}
                        disabled={togglingFreeAccount === selectedUser.user_id}
                        data-testid="modal-toggle-free-btn"
                      >
                        {togglingFreeAccount === selectedUser.user_id ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : selectedUser.has_free_account ? (
                          <UserX className="w-4 h-4 mr-2" />
                        ) : (
                          <Gift className="w-4 h-4 mr-2" />
                        )}
                        {selectedUser.has_free_account ? 'Revoke Free' : 'Grant Free'}
                      </Button>
                    </div>
                  </div>
                  
                  {/* Send Reminder Buttons */}
                  <div className="mt-4 pt-4 border-t border-border">
                    <p className="text-xs text-muted-foreground mb-2">Send Email Reminder</p>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => sendReminderEmail(selectedUser.user_id, 'renewal')}
                        disabled={sendingReminder === `${selectedUser.user_id}-renewal`}
                        data-testid="send-renewal-reminder-btn"
                      >
                        {sendingReminder === `${selectedUser.user_id}-renewal` ? (
                          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                        ) : (
                          <Send className="w-3 h-3 mr-1" />
                        )}
                        Renewal
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => sendReminderEmail(selectedUser.user_id, 'expiring')}
                        disabled={sendingReminder === `${selectedUser.user_id}-expiring`}
                      >
                        {sendingReminder === `${selectedUser.user_id}-expiring` ? (
                          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                        ) : (
                          <Mail className="w-3 h-3 mr-1" />
                        )}
                        Expiring
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-400 hover:text-red-300"
                        onClick={() => sendReminderEmail(selectedUser.user_id, 'failure')}
                        disabled={sendingReminder === `${selectedUser.user_id}-failure`}
                      >
                        {sendingReminder === `${selectedUser.user_id}-failure` ? (
                          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                        ) : (
                          <AlertTriangle className="w-3 h-3 mr-1" />
                        )}
                        Payment Failure
                      </Button>
                    </div>
                  </div>

                  {/* Download Receipt */}
                  {selectedUser.is_subscriber && (
                    <div className="mt-4 pt-4 border-t border-border">
                      <p className="text-xs text-muted-foreground mb-2">Payment Receipts</p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          window.open(`${API_URL}/api/admin/receipts/${selectedUser.user_id}/download`, '_blank');
                        }}
                        data-testid="download-receipt-btn"
                      >
                        <Download className="w-3 h-3 mr-1" />
                        Download Latest Receipt
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Referral Info */}
              <Card className="bg-purple-500/5 border-purple-500/20">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Gift className="w-4 h-4 text-purple-400" />
                    Referral Program
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {/* Who Referred This User */}
                  {selectedUser.referral_info?.referrer_details && (
                    <div className="mb-4 p-3 bg-blue-500/10 border border-blue-500/20 rounded-sm">
                      <p className="text-xs text-blue-400 font-medium mb-2">Referred By</p>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <p className="text-muted-foreground text-xs">Referrer</p>
                          <p className="font-medium">{selectedUser.referral_info.referrer_details.name || selectedUser.referral_info.referrer_details.email}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground text-xs">Code Used</p>
                          <p className="font-mono text-blue-400">{selectedUser.referral_info.referrer_details.referral_code_used}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground text-xs">Referrer Email</p>
                          <p className="text-xs truncate">{selectedUser.referral_info.referrer_details.email}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground text-xs">Discount Received</p>
                          <p className="text-green-400">{selectedUser.referral_info?.program_settings?.referee_credit_percent || 10}% off</p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* This User's Referral Stats */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Their Referral Code</p>
                      <p className="font-mono text-purple-400">{selectedUser.referral_info?.code || 'None'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Users They Referred</p>
                      <p className="font-bold">{selectedUser.referral_info?.total_referred || 0}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Credits Earned</p>
                      <p className="text-green-400">${Number(selectedUser.referral_info?.referral_credits || 0).toFixed(2)}</p>
                    </div>
                  </div>
                  
                  {/* Program Settings Info */}
                  {selectedUser.referral_info?.program_settings && (
                    <div className="mt-3 pt-3 border-t border-border">
                      <p className="text-xs text-muted-foreground mb-1">Program Rates</p>
                      <div className="flex gap-4 text-xs">
                        <span>Referrer earns: <span className="text-green-400">{selectedUser.referral_info.program_settings.referrer_credit_percent}%</span></span>
                        <span>Referee gets: <span className="text-blue-400">{selectedUser.referral_info.program_settings.referee_credit_percent}% off</span></span>
                      </div>
                    </div>
                  )}
                  
                  {/* Referred Users List */}
                  {selectedUser.referral_info?.referred_users?.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-border">
                      <p className="text-xs text-muted-foreground mb-2">Users They Referred ({selectedUser.referral_info.total_referred})</p>
                      <div className="space-y-2 max-h-32 overflow-y-auto">
                        {selectedUser.referral_info.referred_users.slice(0, 5).map((ref, idx) => (
                          <div key={idx} className="flex items-center justify-between text-xs bg-secondary/30 p-2 rounded-sm">
                            <span className="truncate">{ref.email || ref.name}</span>
                            <Badge className={ref.is_subscriber ? "bg-green-500/20 text-green-400" : "bg-gray-500/20 text-gray-400"}>
                              {ref.is_subscriber ? 'Subscriber' : 'Free'}
                            </Badge>
                          </div>
                        ))}
                        {selectedUser.referral_info.referred_users.length > 5 && (
                          <p className="text-xs text-muted-foreground text-center">
                            +{selectedUser.referral_info.referred_users.length - 5} more
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Corrected Games Modal */}
      <Dialog open={showCorrectedGamesModal} onOpenChange={setShowCorrectedGamesModal}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-500" />
              Games Fixed ({correctedGamesList.length})
            </DialogTitle>
            <DialogDescription>
              The following games had their prediction results corrected based on probability.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-2 mt-4">
            {correctedGamesList.map((game, idx) => (
              <div key={idx} className="p-3 bg-slate-800/50 rounded-lg border border-slate-700/50">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">{game.league}</Badge>
                      <span className="text-sm font-medium">{game.matchup}</span>
                    </div>
                    <div className="text-xs text-slate-400 mt-1">
                      Probability: Home {game.home_probability}% vs Away {game.away_probability}%
                      {' | '}Predicted: {game.predicted_winner === 'home' ? 'Home' : 'Away'}
                      {' | '}Actual: {game.actual_winner === 'home' ? 'Home' : 'Away'}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-red-500/20 text-red-400 text-xs">
                      {game.old_result}
                    </Badge>
                    <span className="text-slate-500">→</span>
                    <Badge className="bg-green-500/20 text-green-400 text-xs">
                      {game.new_result}
                    </Badge>
                  </div>
                </div>
              </div>
            ))}
            
            {correctedGamesList.length === 0 && (
              <div className="text-center text-slate-400 py-8">
                No games needed correction
              </div>
            )}
          </div>
          
          <div className="flex justify-end mt-4">
            <Button onClick={() => setShowCorrectedGamesModal(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Wrap with ErrorBoundary for production stability
export default function AdminDashboardWithErrorBoundary() {
  return (
    <ErrorBoundary>
      <AdminDashboard />
    </ErrorBoundary>
  );
}
